import { format, isDate } from 'date-fns';
import {
  Activity,
  ActivitySession,
  ActivitySessionAttendees,
  ActivitySessionDetails,
  Cohort,
  Declaration,
  FrameworkAnswer,
  FrameworkResultForm,
  Indicator,
  IndicatorBenchmark,
  IndicatorCategory,
  IndicatorDimension,
  IndicatorPeriodDate,
  IndicatorTarget,
  Institution,
  MonitoringForm,
  MonthlyCountData,
  OrganisationType,
  Programme,
  ProvidedDocument,
  Template,
  Reporter,
  MonitoringFormUsersType,
  MonitoringFormDueDateType,
  MonitoringFormAssociatedFormType,
  MonitoringFormAssociatedActivityType,
  MonitoringFormIndicatorType,
  MonitoringFormReporterType,
  Option,
  Portfolio,
  UserInterface,
  WorkspaceFormSubmission,
  WorkspaceFormSubmissionAnswer,
  ECDEnrolmentData,
  ECDStaffData,
  ECDFacilityDetail,
  PeopleProfileFormType,
  OptionNumber,
  PeopleProfileInstitutionFormType,
  LayoutsInsightsType,
  UserOrganisationFormType,
  PeopleProfileOrganisationFormType,
  CaregiverType,
  InsightsOrganisationFormType,
  IndicatorProgressStatusCondition,
} from './types';
import { PROGRAMME_OVERVIEW_ISARCHIVED_OPTIONS } from '../consts';
import { isValidDate } from './validator';
import { calculateAge, determineAgeRange } from '../containers/shared/peopleProfiles/forms/PersonDetailsForm';
import { isEmpty } from '.';
import { ProgrammeMonitoringFormDataType } from '../containers/shared/myForms/types';

export const formatPassRateData = (data: any[]) => {
  const avg = Array.from(
    data.reduce(
      (acc, obj) =>
        Object.keys(obj).reduce(
          (acc, key) =>
            typeof obj[key] == 'number'
              ? acc.set(
                  key,
                  // immediately invoked function:
                  (([sum, count]) => [sum + obj[key], count + 1])(acc.get(key) || [0, 0])
                ) // pass previous value
              : acc,
          acc
        ),
      new Map()
    ),
    ([name, [sum, count]]) => {
      if (name === 'firstPassRate') {
        return { name: 'T1', average: sum / count };
      }
      if (name === 'secondPassRate') {
        return { name: 'T2', average: sum / count };
      }
      if (name === 'thirdPassRate') {
        return { name: 'T3', average: sum / count };
      }
      return { name: 'T4', average: sum / count };
    }
  );
  return avg;
};

/**
 * This function will format a string from an enum,
 * making only the first letter a capital and making
 * the rest lower case, it also removes underscores
 * and replacing them with spaces.
 * @param {string} toFix - The string we are wanting
 * to format.
 * @param firstLetterCaps
 * in the string will get a capital first letter.
 */
export function formatString(toFix: string, firstLetterCaps: boolean): string {
  try {
    if (firstLetterCaps) {
      let sentence = '';
      const temp = toFix
        .toLowerCase()
        .replace(/_/g, ' ')
        .split(' ');
      temp.forEach(word => (sentence += word.charAt(0).toUpperCase() + word.substr(1).toLowerCase() + ' '));
      return sentence;
    }
    return (
      toFix.charAt(0).toUpperCase() +
      toFix
        .substr(1)
        .toLowerCase()
        .replace(/_/g, ' ')
    );
  } catch (error) {
    return toFix;
  }
}

/**
 * This function will format a string from an enum,
 * making only the first letter a capital and making
 * the rest lower case, it also removes underscores
 * and replacing them with spaces.
 * @param {string} toFix - The string we are wanting
 * to format.
 * @param firstLetterCaps
 * in the string will get a capital first letter.
 */
export function formatStringToEnum(toFix: string): string {
  try {
    let sentence = '';
    const temp = toFix
      .toUpperCase()
      .replace(' ', '_')
      .split(' ');
    temp.forEach(word => (sentence += word.toUpperCase() + '_'));
    return sentence.slice(0, -1);
  } catch (error) {
    return toFix;
  }
}

/**
 * This function serves to mutate an enum value which is
 * being returned from the backend into an object that's compatible
 * with react-select.
 * @param { string | { label: string, value: string } } data - the variable
 * to either be returned if it's already a select object,
 * or to be made into one if it isn't.
 */
export function formatSelectValue(
  data: string | { label: string; value: string } | number | boolean,
  list?: { label: string; value: any }[]
): { label: string; value: string } {
  try {
    if (data !== undefined && data !== null && typeof data !== 'object') {
      if (list && list.length > 0) {
        const i = list.findIndex(o => o.value.toString() === data.toString());
        if (i > -1) return list[i];
      }
      return {
        label: formatString(data.toString(), true),
        value: data.toString(),
      };
    } else if (data && typeof data === 'object' && 'label' in data && 'value' in data) {
      return data;
    }
    return { label: '', value: '' };
  } catch (error) {
    return { label: '', value: '' };
  }
}

/**
 * Formats Apollo Query/Mutation logger messages
 */
export function formatMessage(operationType: any, operation: any, elapsed?: any) {
  const headerCss = [
    'color: gray; font-weight: lighter', // title
    `color: ${operationType === 'query' ? '#02B875' : '#03A9F4'};`, // operationType
    'color: #000;', // operationName
  ];

  const parts = ['%c apollo'];

  parts.push(`%c${operationType}`);
  parts.push(`%c${operation.operationName}`);

  if (elapsed) {
    parts.push(`%c(in ${elapsed} ms)`);
    headerCss.push('color: gray; font-weight: lighter;'); // time
  }

  return [parts.join(' '), ...headerCss];
}

/**
 * Formats Apollo Query/Mutation logger errors.
 */
export function formatError(message: any, path?: any) {
  if (path) {
    const headerCss = [
      'color: gray; font-weight: lighter', // title
      'color: #333; font-weight: 600', // message
      'color: gray; font-weight: lighter', // on
      'color: #333; font-weight: 600', // path
      'color: gray; font-weight: lighter', // request.
    ];

    const parts = ['%c message'];

    parts.push(`%c${message}`);
    parts.push('%con');
    parts.push(`%c${path.join('.')}`);
    parts.push('%crequest');

    return [parts.join(' '), ...headerCss];
  }

  const headerCss = [
    'color: gray; font-weight: lighter', // title
    'color: #333; font-weight: 600', // message
  ];

  const parts = ['%c message'];

  parts.push(`%c${message}`);

  return [parts.join(' '), ...headerCss];
}

// Format value from db in ui friendly way
export const formatDBValue = (item: any) => {
  if (item === null) {
    return '';
  }
  if (typeof item === 'boolean') {
    return item ? 'Yes' : 'No';
  }

  if (isDate(item) || (typeof item === 'string' && item.endsWith('.000Z'))) {
    return format(new Date(item), 'dd-MM-yyyy');
  }

  if (typeof item === 'string') {
    return item || '-';
  }

  if (typeof item === 'object' && 'name' in item) {
    return item.name || '-';
  }

  return item || '-';
};

// Strip names from objects from db
export const stripDBNames = (myData: object) => {
  try {
    const data = Object.assign({}, myData);
    Object.keys(myData).forEach((item: any) => {
      let val = data[item];

      if (Boolean(Array.isArray(val))) {
        data[item] = val.map((obj: any) => {
          if (Boolean(Array.isArray(obj))) {
            return stripDBNames(obj);
          }

          if ('id' in obj && 'name' in obj) {
            return obj.name;
          }

          return stripDBNames(obj);
        });
      }

      if (val && typeof val === 'object' && 'id' in val && 'name' in val) {
        const name = val.name;
        data[item] = name;
      }
    });
    return data;
  } catch (error) {
    return {};
  }
};

/**
 * formats values for react-select
 * @param value
 * @returns Option | undefined
 */
export const formatValues = (
  value: { name: string; id: string } | string | boolean | Option | undefined | null
): Option | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'string') {
    return formatSelectValue({
      label: value,
      value: value,
    });
  }
  if (typeof value === 'boolean') {
    return formatSelectValue({
      label: value ? 'Yes' : 'No',
      value: value.toString(),
    });
  }
  if (typeof value === 'object' && 'label' in value && 'value' in value) {
    return value;
  }

  return formatSelectValue({
    label: value.name,
    value: value.id,
  });
};

/**
 * formats values for react-select
 * @param value
 * @returns OptionNumber | undefined
 */
export const formatNumberValues = (
  value: { name: string; id: number } | number | undefined | null
): OptionNumber | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'number') {
    return {
      label: value.toString(),
      value: value,
    };
  }
  return {
    label: value.name,
    value: value.id,
  };
};

/**
 * formats values for react-select
 * @param value
 * @returns formattedValue | undefined
 */
export const formatUserValues = (value: { firstName: string; lastName: string; id: string }) => {
  return value
    ? formatSelectValue({
        label: `${value.firstName} ${value.lastName}`,
        value: value.id,
      })
    : undefined;
};

export const formatArrayUserValues = (array: { firstName: string; lastName: string; id: string }[]) => {
  return array && Boolean(Array.isArray(array))
    ? array.map(value =>
        formatSelectValue({
          label: `${value.firstName} ${value.lastName}`,
          value: value.id,
        })
      )
    : undefined;
};

/**
 * formats array of values for react-select
 * @param value[]
 * @returns formattedValue[] | undefined
 */
export const formatArrayValues = (array: { name: string; id: string }[]) => {
  return array && Boolean(Array.isArray(array))
    ? array.map(value =>
        formatSelectValue({
          label: value.name,
          value: value.id,
        })
      )
    : undefined;
};

const formatTime = (value: any): Date => {
  let isValidDate = Date.parse(value);
  if (isNaN(isValidDate)) {
    return new Date('October 13, 2014 ' + value);
  } else {
    return new Date(value);
  }
};

export const formatBoolean = (value: any, defaultValue: boolean): boolean => {
  if (value === undefined || value === null) return defaultValue;
  return value as boolean;
};

export const formatOrganisationName = (organisation: OrganisationType): string => {
  if (organisation.isFunder && organisation.funder) return organisation.funder.name;
  if (organisation.isInstitution && organisation.institution) return organisation.institution.name;
  if (organisation.isServiceProvider && organisation.serviceProvider) return organisation.serviceProvider.name;
  return '';
};

export const formatOrganisationArrayValues = (
  array: OrganisationType[]
): { label: string; value: string }[] | undefined => {
  return array && Boolean(Array.isArray(array))
    ? array.map(item => {
        const { id, isFunder, isInstitution, isServiceProvider } = item;

        if (isFunder && item.funder) return formatSelectValue({ label: item.funder.name, value: id });
        if (isInstitution && item.institution) return formatSelectValue({ label: item.institution.name, value: id });
        if (isServiceProvider && item.serviceProvider)
          return formatSelectValue({ label: item.serviceProvider.name, value: id });

        return formatSelectValue({
          label: '',
          value: '',
        });
      })
    : undefined;
};

export const formatReporterAssigneeArrayValues = (
  array: Reporter[]
): { label: string; value: string }[] | undefined => {
  return array && Boolean(Array.isArray(array))
    ? array
        .filter(item => item.assignee && item.assignee.id)
        .map(item => {
          const { id, assignee } = item;

          if (assignee && item && item.assignee) {
            return formatSelectValue({ label: item.assignee.firstName + ' ' + item.assignee.lastName, value: id });
          }
          return formatSelectValue({
            label: '',
            value: '',
          });
        })
    : undefined;
};

export const formatUserData = (user: UserInterface) => {
  const isAdmin =
    user.roles &&
    (user.roles.filter(obj => obj.name === 'SUPERADMIN').length > 0 ||
      user.roles.filter(obj => obj.name === 'ADMIN').length > 0);

  return {
    confirmPassword: '',
    contactNumber: user.contactNumber || '',
    designation: user.designation || '',
    email: user.email || '',
    firstName: user.firstName || '',
    funderId: user.funder ? formatValues(user.funder) : undefined,
    gender: user.gender
      ? formatValues({
          id: user.gender,
          name: user.gender,
        })
      : undefined,
    idNum: user.idNum || '',
    institutionId: user.institution ? formatValues(user.institution) : undefined,
    lastName: user.lastName || '',
    password: '',
    planId: user.plan ? formatValues(user.plan) : undefined,
    registrationRoleId: user.registrationRole ? formatValues(user.registrationRole) : undefined,
    roleIds: user.roles ? formatArrayValues(user.roles) : [],
    serviceProviderId: user.serviceProvider ? formatValues(user.serviceProvider) : undefined,
    status: user.status
      ? formatValues({
          id: user.status,
          name: formatString(user.status, true),
        })
      : undefined,
    title: user.title
      ? formatValues({
          id: user.title,
          name: formatString(user.title, true),
        })
      : undefined,
    organisations: (user.organisations && formatUserOrganisations(user.organisations)) || undefined,
    persalNumber: user.persalNumber || '',
    isAdminUser: isAdmin || false,
  };
};

export const formatUserOrganisations = (organisations: any[]): UserOrganisationFormType[] => {
  return organisations.map(obj => {
    return {
      id: obj.id,
      systemRole: formatValues(obj.role),
      practitionerType: obj.practitionerType
        ? formatSelectValue({
            value: obj.practitionerType,
            // DIETICIAN keeps its stored value but now displays as "Dietitian" (IB-1765).
            label: obj.practitionerType === 'DIETICIAN' ? 'Dietitian' : formatString(obj.practitionerType, true),
          })
        : undefined,
      registrationRole: formatValues(obj.registrationRole),
      dataRole: formatValues(obj.dataRole),
      status: formatValues(obj.status),
      isDefault: obj.isDefault,
      dateCreated: obj.createdAt ? new Date(obj.createdAt) : new Date(),
      dateLastModified: obj.updatedAt ? new Date(obj.updatedAt) : new Date(),
      dateArchived: obj.dateArchived,
      organisation: obj.organisation
        ? {
            id: obj.organisation.id,
            isFunder: obj.isFunder,
            isServiceProvider: obj.isServiceProvider,
            isInstitution: obj.isInstitution,
            funder: obj.organisation.funder
              ? {
                  id: obj.organisation.funder.id,
                  name: obj.organisation.funder.name,
                }
              : undefined,
            serviceProvider: obj.organisation.serviceProvider
              ? {
                  id: obj.organisation.serviceProvider.id,
                  name: obj.organisation.serviceProvider.name,
                }
              : undefined,
            institution: obj.organisation.institution
              ? {
                  id: obj.organisation.institution.id,
                  name: obj.organisation.institution.name,

                  type: obj.organisation.institution.type
                    ? {
                        id: obj.organisation.institution.type.id,
                        name: obj.organisation.institution.type.name,
                      }
                    : undefined,
                }
              : undefined,
          }
        : undefined,
    };
  });
};

export const formatInsightOrganisations = (organisations: any[]): InsightsOrganisationFormType[] => {
  try {
    const result = organisations.map(obj => {
      const checkedUsers = {};

      if (obj.users && obj.users.length > 0) {
        obj.users.forEach((a: any) => {
          if (a.id) {
            checkedUsers[a.id] = true;
          }
        });
      }

      return {
        id: obj.id,
        status: formatValues(obj.status),
        dateCreated: obj.createdAt ? new Date(obj.createdAt) : new Date(),
        dateArchived: obj.dateArchived ? new Date(obj.dateArchived) : undefined,
        users: checkedUsers,
        organisation: obj.organisation
          ? {
              id: obj.organisation.id,
              isFunder: obj.isFunder,
              isServiceProvider: obj.isServiceProvider,
              isInstitution: obj.isInstitution,
              isAdmin: obj.organisation.isAdmin,
              funder: obj.organisation.funder
                ? {
                    id: obj.organisation.funder.id,
                    name: obj.organisation.funder.name,
                  }
                : undefined,
              serviceProvider: obj.organisation.serviceProvider
                ? {
                    id: obj.organisation.serviceProvider.id,
                    name: obj.organisation.serviceProvider.name,
                  }
                : undefined,
              institution: obj.organisation.institution
                ? {
                    id: obj.organisation.institution.id,
                    name: obj.organisation.institution.name,

                    type: obj.organisation.institution.type
                      ? {
                          id: obj.organisation.institution.type.id,
                          name: obj.organisation.institution.type.name,
                        }
                      : undefined,
                  }
                : undefined,
            }
          : undefined,
      };
    });

    return result;
  } catch (error) {
    console.log('error', error);
    return [];
  }
};

export const formatIndicators = (indicators: any[]): Indicator[] => {
  const getIndicatorDimensions = (dimensions: any[]): IndicatorDimension[] => {
    if (!dimensions) return [];
    const sorted = JSON.parse(JSON.stringify(dimensions)).sort((a: any, b: any) => a.index - b.index);
    return sorted.map((obj: any, index: number) => {
      return {
        id: obj.id,
        index: obj.index,
        name: {
          value: obj.name,
          label: obj.name,
        },
        options: obj.options
          ? obj.options.map((opt: string) => {
              return { value: opt, label: opt };
            })
          : [],
      };
    });
  };

  const getIndicatorBenchmarks = (benchmarks: any[]): IndicatorBenchmark[] => {
    if (!benchmarks) return [];
    const sorted = JSON.parse(JSON.stringify(benchmarks)).sort((a: any, b: any) => a.index - b.index);
    return sorted.map((obj: any, index: number) => {
      return {
        id: obj.id,
        index: obj.index,
        name: obj.name,
        description: obj.description,
      };
    });
  };

  const getIndicatorPeriods = (periods: any[]): IndicatorPeriodDate[] => {
    if (!periods) return [];
    const sorted = JSON.parse(JSON.stringify(periods)).sort((a: any, b: any) => a.periodNumber - b.periodNumber);
    return sorted.map((obj: any, index: number) => {
      return {
        id: obj.id,
        periodDate: obj.periodDate,
        periodNumber: obj.periodNumber,
        label: obj.label,
      };
    });
  };

  const getIndicatorTargets = (targets: any[]): IndicatorTarget[] => {
    return targets.map(obj => {
      return {
        id: obj.id,
        value: obj.value,
        periodDate: obj.periodDate,
        periodNumber: obj.periodNumber,
        dimensionOptions: obj.dimensionOptions,
        site: obj.site,
      };
    });
  };

  const getIndicatorCategories = (categories: any[]): IndicatorCategory[] => {
    if (!categories) return [];
    return categories
      .filter(obj => obj.category && obj.option)
      .map(obj => {
        return {
          id: obj.id,
          groupId: obj.category.id,
          groupName: obj.category.name,
          archived: obj.category.status === 'ARCHIVED',
          option: { value: obj.option.id, label: obj.option.name },
        };
      });
  };

  // Keyed by group so the Categorisation section can look a group's selection up directly, whether
  // that group is still active or only survives as an archived value on this indicator.
  const getIndicatorCategorySelections = (categories: any[]): { [groupId: string]: Option | null } => {
    if (!categories) return {};
    return categories.reduce((selections: { [groupId: string]: Option | null }, obj: any) => {
      if (obj.category && obj.option) {
        selections[obj.category.id] = { value: obj.option.id, label: obj.option.name };
      }
      return selections;
    }, {});
  };

  const getProgressStatusOptions = (progressStatusConditions: any): IndicatorProgressStatusCondition[] => {
    return progressStatusConditions && progressStatusConditions.length
      ? progressStatusConditions.map((progressStatusOption: any) => {
          return {
            id: progressStatusOption.id,
            index: progressStatusOption.index,
            status: progressStatusOption.status && {
              value: progressStatusOption.status,
              label: progressStatusOption.status,
            },
            value1: progressStatusOption.value1,
            value2: progressStatusOption.value2,
            operator: progressStatusOption.operator && {
              value: progressStatusOption.operator,
              label: progressStatusOption.operator,
            },
          };
        })
      : [];
  };

  return indicators.map(obj => {
    return {
      id: obj.id,
      measurementUnit: obj.measurementUnit,
      unit: obj.unit,
      name: obj.name,
      indicatorLabel: obj.indicatorLabel,
      startDate: obj.startDate,
      endDate: obj.endDate,
      target: obj.target,
      targetValue: obj.targetValue,
      description: obj.description,
      resultRelationship: obj.resultRelationship,
      hasTargets: obj.hasTargets,
      hasBaseline: obj.hasBaseline,
      minValueAllowed: obj.minValueAllowed,
      maxValueAllowed: obj.maxValueAllowed,
      type: obj.type && { value: obj.type, label: obj.type },
      dataSources: obj.dataSources ? obj.dataSources.join() : '',
      outcomeTheme: obj.outcomeTheme && { value: obj.outcomeTheme.id, label: obj.outcomeTheme.name },
      period: obj.period && { value: obj.period.id, label: obj.period.name },
      dimensions: getIndicatorDimensions(obj.dimensions),
      benchmarks: getIndicatorBenchmarks(obj.benchmarks),
      periods: getIndicatorPeriods(obj.periods),
      targets: getIndicatorTargets(obj.targets),
      dataSource: obj.dataSource && { value: obj.dataSource, label: obj.dataSource },
      dataSourceOption: obj.dataSourceOption && { value: obj.dataSourceOption, label: obj.dataSourceOption },
      dataSourceOptionName: obj.datasetDataSource && {
        value: obj.datasetDataSource.id,
        label: obj.datasetDataSource.description,
      },
      calculation: obj.calculation,
      calculationMethod: obj.calculationMethod && {
        value: obj.calculationMethod,
        label: obj.calculationMethod,
      },
      dataSourceWorkspaceId: obj.dataSourceWorkspace && {
        value: obj.dataSourceWorkspace.id,
        label: obj.dataSourceWorkspace.name,
      },
      includeEmptyRows: obj.includeEmptyRows,
      baselineOrTargetValue: obj.baselineOrTargetValue && {
        value: obj.baselineOrTargetValue,
        label: obj.baselineOrTargetValue,
      },
      baselineDate: obj.baselineDate,
      baselineValue: obj.baselineValue,
      trackedByOrganisation: obj.trackedByOrganisation,
      trackingOrganisation: obj.trackingOrganisation && {
        value: obj.trackingOrganisation,
        label: obj.trackingOrganisation,
      },
      trackingHierarchy: obj.trackingHierarchy && {
        value: obj.trackingHierarchy.id,
        label: obj.trackingHierarchy.name,
      },
      trackingHierarchyLevel: obj.trackingHierarchyLevel && {
        value: obj.trackingHierarchyLevel.id,
        label: obj.trackingHierarchyLevel.name,
      },
      sites: obj.sites ? obj.sites : [],
      timeline: obj.timeline && { value: obj.timeline, label: obj.timeline },
      trackProgressStatus: obj.trackProgressStatus,
      statusTier: obj.statusTier && { value: obj.statusTier, label: obj.statusTier },
      statusType: obj.statusType && { value: obj.statusType, label: obj.statusType },
      calculateStatus: obj.calculateStatus,
      basedOn: obj.basedOn && { value: obj.basedOn, label: obj.basedOn },
      allStatusOverides: obj.allStatusOverides,
      progressStatusConditions: getProgressStatusOptions(obj.progressStatusConditions),
      indicatorPeriodType: obj.indicatorPeriodType && {
        value: obj.indicatorPeriodType,
        label: obj.indicatorPeriodType,
      },
      periodGroup: obj.periodGroup && { value: obj.periodGroup.id, label: obj.periodGroup.name },
      firstPeriodlabel: obj.firstPeriod && {
        value: obj.firstPeriod.id,
        label: obj.firstPeriod.label,
        otherField: obj.firstPeriod.periodDate,
      },
      firstPeriodDate: obj.firstPeriod && obj.firstPeriod.periodDate,
      periodsCount: obj.periodsCount,
      applyCurrencyConversion: obj.applyCurrencyConversion,
      exchangeRate: obj.exchangeRate && { value: obj.exchangeRate, label: obj.exchangeRate },
      currencyGroup: obj.currencyGroup && { value: obj.currencyGroup.id, label: obj.currencyGroup.name },
      categories: getIndicatorCategories(obj.categories),
      categorySelections: getIndicatorCategorySelections(obj.categories),
    };
  });
};

export const formatFrameworkResults = (frameworkResults: any[], parentId: string | null): FrameworkResultForm[] => {
  return frameworkResults
    .filter(
      obj => (parentId === null && obj.parentResult === null) || (obj.parentResult && obj.parentResult.id === parentId)
    )
    .map(obj => {
      return {
        id: obj.id,
        type: { value: obj.level.id, label: obj.type },
        defaultId: obj.default ? obj.default.id : '',
        number: obj.number,
        description: obj.description,
        objectiveName: obj.objectiveName,
        level: obj.level.level,
        childResults: formatFrameworkResults(frameworkResults, obj.id),
        indicators: formatIndicators(obj.indicators),
        expanded: false,
      };
    });
};

export const formatCurrency = (childCurrency: string, parentCurrency: string) => {
  if (!childCurrency && !parentCurrency) return '';
  if (parentCurrency !== '') return formatSelectValue(parentCurrency);
  return formatSelectValue(childCurrency);
};

export const getOrganisationId = (org: {
  id: string;
  funder?: {
    id: string;
  } | null;
  serviceProvider?: {
    id: string;
  } | null;
  institution?: {
    id: string;
  } | null;
}): string => {
  if (org.funder) return org.funder.id;
  if (org.serviceProvider) return org.serviceProvider.id;
  if (org.institution) return org.institution.id;
  return '';
};

export const getOrganisationName = (org: {
  id: string;
  funder?: {
    name: string;
  } | null;
  serviceProvider?: {
    name: string;
  } | null;
  institution?: {
    name: string;
  } | null;
}): string => {
  if (org.funder) return org.funder.name;
  if (org.serviceProvider) return org.serviceProvider.name;
  if (org.institution) return org.institution.name;
  return '';
};

export const getOrganisationOption = (org: {
  id: string;
  funder?: {
    id: string;
    name: string;
  } | null;
  serviceProvider?: {
    id: string;
    name: string;
  } | null;
  institution?: {
    id: string;
    name: string;
  } | null;
}): Option => {
  return {
    value: getOrganisationId(org),
    label: getOrganisationName(org),
  };
};

export const getUserOption = (user: { id: string; firstName?: string; lastName?: string }): Option => {
  const { id, firstName, lastName } = user;

  return {
    value: id,
    label: firstName && lastName ? `${firstName} ${lastName}` : firstName ? firstName : lastName ? lastName : '',
  };
};

export const formatMonitoringForms = (forms: any[]): MonitoringForm[] => {
  const getFormUsers = (users: any[]): MonitoringFormUsersType[] => {
    return users.map(obj => {
      let organisation: Option = { value: '', label: '' };
      if (obj.organisation) {
        if (obj.organisation.funder) {
          organisation = { value: obj.organisation.id, label: obj.organisation.funder.name };
        } else if (obj.organisation.serviceProvider) {
          organisation = {
            value: obj.organisation.id,
            label: obj.organisation.serviceProvider.name,
          };
        } else if (obj.organisation.institution) {
          organisation = { value: obj.organisation.id, label: obj.organisation.institution.name };
        }
      }
      return {
        id: obj.id,
        user: { value: obj.user.id, label: `${obj.user.firstName} ${obj.user.lastName}` },
        reporter: obj.reporter
          ? obj.reporter.map((r: any) => {
              if (r.funder) {
                return { value: r.id, label: r.funder.name };
              } else if (r.serviceProvider) {
                return {
                  value: r.id,
                  label: r.serviceProvider.name,
                };
              } else if (r.institution) {
                return { value: r.id, label: r.institution.name };
              } else {
                return undefined;
              }
            })
          : [],
        organisation: organisation,
        formRole: { value: obj.formRole.id, label: obj.formRole.name },
        sites: obj.sites ? obj.sites.map((a: any) => a.id) : [],
      };
    });
  };

  const getFormDueDates = (dueDates: any[]): MonitoringFormDueDateType[] => {
    const result: MonitoringFormDueDateType[] = dueDates.map(obj => {
      return {
        id: obj.id,
        date: obj.date ? new Date(obj.date) : new Date(),
        periodNumber: obj.periodNumber,
      };
    });
    return result.sort((a, b) => (a.date ? a.date.getTime() : 0) - (b.date ? b.date.getTime() : 0));
  };

  const getFormAssociatedForms = (forms: any[]): MonitoringFormAssociatedFormType[] => {
    return forms.map(obj => {
      return {
        id: obj.id,
        form: { value: obj.id, label: obj.name },
        workspace: { value: '', label: '' },
        published: obj.published ? true : false,
      };
    });
  };

  const getFormAssociatedActivities = (activities: any[]): MonitoringFormAssociatedActivityType[] => {
    return activities.map(obj => {
      return {
        id: obj.id,
        activity: { value: obj.id, label: obj.name },
        intervention: { value: obj.intervention.id, label: obj.intervention.name },
      };
    });
  };

  const getFormIndicators = (indicators: any[]): MonitoringFormIndicatorType[] => {
    return indicators.map(obj => {
      return {
        id: obj.id,
        indicatorId: obj.indicator.id,
        frameworkResultId: obj.indicator.frameworkResult[0].id,
        dimensions: obj.dimensions ? obj.dimensions : [],
        periodId: obj.indicator.period ? obj.indicator.period.id : '',
        periodName: obj.indicator.period ? obj.indicator.period.name : '',
        indicatorName: obj.indicator.name,
        trackingHierarchyId: obj.indicator.trackingHierarchy && obj.indicator.trackingHierarchy.id,
        trackingHierarchyLevelId: obj.indicator.trackingHierarchyLevel && obj.indicator.trackingHierarchyLevel.id,
        siteName: obj.site ? obj.site.name : '',
        siteId: obj.site ? obj.site.id : '',
      };
    });
  };

  const getFormReporters = (reporters: any[]): MonitoringFormReporterType[] => {
    return reporters.map(obj => {
      let organisation: Option = { value: '', label: '' };
      if (obj.organisation && obj.organisation.funder) {
        organisation = { value: obj.organisation.id, label: obj.organisation.funder.name };
      } else if (organisation && obj.organisation.serviceProvider) {
        organisation = {
          value: obj.organisation.id,
          label: obj.organisation.serviceProvider.name,
        };
      } else if (obj.organisation && obj.organisation.institution) {
        organisation = { value: obj.organisation.id, label: obj.organisation.institution.name };
      }

      return {
        id: obj.id,
        organisation: organisation,
        sites: obj.sites ? obj.sites.map((a: any) => a.id) : [],
      };
    });
  };

  const getFormCurrencies = (currencies: any[]) => {
    return currencies
      .filter(obj => obj.currencyGroup)
      .map(obj => ({
        id: obj.id,
        currencyGroupId: obj.currencyGroup.id,
        name: obj.currencyGroup.name,
        exchangeRate: obj.currencyGroup.exchangeRate,
      }));
  };

  return forms.map(obj => {
    return {
      id: obj.id,
      name: obj.name,
      description: obj.description,
      captureValue: { label: obj.captureValue, value: obj.captureValue },
      period: { label: obj.period ? obj.period.name : '', value: obj.period ? obj.period.id : '' },
      version: obj.version || 0,
      published: obj.published || false,
      indicators: obj.indicators ? getFormIndicators(obj.indicators) : [],
      users: obj.users ? getFormUsers(obj.users) : [],
      dueDates: obj.dueDates ? getFormDueDates(obj.dueDates) : [],
      associatedForms: obj.associatedForms ? getFormAssociatedForms(obj.associatedForms) : [],
      associatedActivities: obj.associatedActivities ? getFormAssociatedActivities(obj.associatedActivities) : [],
      reporters: obj.reporters ? getFormReporters(obj.formReporters) : [],
      supportingDocuments: obj.supportingDocuments || false,
      allowCurrency: obj.allowCurrency || false,
      currencies: obj.currencies ? getFormCurrencies(obj.currencies) : [],
    };
  });
};

export const formatDimensionOptionsCaching = (options: {}, results: FrameworkResultForm[]) => {
  results.forEach(result => {
    result.indicators.forEach(indicator => {
      indicator.dimensions.forEach(dimension => {
        if (dimension.name && dimension.name.label) {
          if (options[dimension.name.label] === undefined) {
            options[dimension.name.label] = {
              name: dimension.name.label,
              query: '',
              options: [],
            };
          }
          dimension.options.forEach(o => {
            if (dimension.name && !options[dimension.name.label].options.includes(o.label)) {
              options[dimension.name.label].options.push(o.label);
            }
          });
        }
      });
    });
    formatDimensionOptionsCaching(options, result.childResults);
  });
};

export const formatProgrammeData = (programme: Programme, organisationId: string) => {
  const { id, frameworkResults, monitoringForms, ...rest } = programme;

  const getPortfolios = (portfolios: Portfolio[], parentPortfolios: Portfolio[], organisationId: string) => {
    if (!portfolios && !parentPortfolios) return [];
    const allPortfolios = new Set([...portfolios, ...parentPortfolios]);

    return Array.from(allPortfolios)
      .filter(obj => obj.owner && obj.owner.id === organisationId)
      .map(obj => {
        return {
          label: obj.name,
          value: obj.id,
        };
      });
  };

  var result = {
    id,
    overview: {
      name: rest.name || '',
      summary: rest.summary || '',
      theoryOfChange: rest.theoryOfChange !== null ? rest.theoryOfChange : '',
      fromDate: rest.fromDate,
      toDate: rest.toDate,
      isArchived:
        rest.isArchived !== null &&
        formatSelectValue(
          rest.isArchived === undefined ? false : rest.isArchived,
          PROGRAMME_OVERVIEW_ISARCHIVED_OPTIONS
        ),
      serviceProviderIds: rest.serviceProviders && formatArrayValues(rest.serviceProviders),
      frameworkTypeId: rest.frameworkType !== null ? formatValues(rest.frameworkType) : null,
      geographies: rest.geographies
        ? rest.geographies.map(item => {
            return {
              value: item.id,
              label: item.name,
            };
          })
        : [],
      reporters: rest.reporters
        ? rest.reporters.map(item => {
            return {
              id: item.id,
              organisation: item.organisation,
              organisationName: formatOrganisationName(item.organisation),
              assignee: item.assignee,
              value: item.id,
              label: formatOrganisationName(item.organisation),
            };
          })
        : [],
      assignees: rest.reporters && formatReporterAssigneeArrayValues(rest.reporters),
      ownerId: (rest.owner && formatUserValues(rest.owner)) || '',
      parentProgrammeId: rest.parentProgramme && formatValues(rest.parentProgramme),
      childProgrammeIds: rest.childProgrammes && formatArrayValues(rest.childProgrammes),
      portfolioIds: getPortfolios(
        rest.portfolios ? rest.portfolios : [],
        rest.parentProgramme && rest.parentProgramme.portfolios ? rest.parentProgramme.portfolios : [],
        organisationId || ''
      ),
      theoryOfChangeFileId: (rest.theoryOfChangeFile && rest.theoryOfChangeFile.id) || '',
      budgetAmount: rest.budgetAmount || 0,
      currency: formatCurrency(rest.currency || '', rest.parentProgramme ? rest.parentProgramme.currency || '' : ''),
      addOnIds: rest.addOns && formatArrayValues(rest.addOns),
      childBudget:
        rest.childProgrammes && rest.childProgrammes.length > 0
          ? rest.childProgrammes
              .map((item: any) => {
                return item.budgetAmount ? item.budgetAmount : 0;
              })
              .reduce((prev, curr) => prev + curr)
          : 0,
    },
    indicators: {
      frameworkResults: frameworkResults ? formatFrameworkResults(frameworkResults, null) : [],
      dimensionOptions: {},
    },
    monitoring: {
      forms: monitoringForms ? formatMonitoringForms(monitoringForms) : [],
    },
  };
  formatDimensionOptionsCaching(result.indicators.dimensionOptions, result.indicators.frameworkResults);
  return result;
};
export const formatIndicatorFrameworkData = (indicatorFramework: any) => {
  const { id, frameworkResults, monitoringForms, ...rest } = indicatorFramework;

  let reporters: any = [];

  if (rest.programme && rest.programme.reporters) {
    rest.programme.reporters.forEach((item: any) => {
      reporters.push({
        id: item.id,
        organisation: item.organisation,
        organisationName: formatOrganisationName(item.organisation),
        assignee: item.assignee,
        value: item.id,
        label: formatOrganisationName(item.organisation),
      });
    });
  }

  if (rest.intervention && rest.intervention.reporters) {
    rest.intervention.reporters.forEach((item: any) => {
      reporters.push({
        id: item.id,
        organisation: item.organisation,
        organisationName: formatOrganisationName(item.organisation),
        assignee: item.assignee,
        value: item.id,
        label: formatOrganisationName(item.organisation),
      });
    });
  }

  if (rest.reporters && rest.reporters.length > 0) {
    rest.reporters.forEach((item: any) => {
      reporters.push({
        id: item.id,
        organisation: item.organisation,
        organisationName: formatOrganisationName(item.organisation),
        assignee: item.assignee,
        value: item.id,
        label: formatOrganisationName(item.organisation),
      });
    });
  }

  const uniqueObject = reporters.reduce((acc: any, item: any) => {
    acc[item.id] = item;
    return acc;
  }, {});

  const uniqueReporters = Object.values(uniqueObject);

  var result = {
    id,
    overview: {
      reporters: uniqueReporters,
      frameworkTypeId: rest.frameworkType !== null ? formatValues(rest.frameworkType) : null,
    },
    indicators: {
      frameworkResults: frameworkResults ? formatFrameworkResults(frameworkResults, null) : [],
      dimensionOptions: {},
    },
    monitoring: {
      forms: monitoringForms ? formatMonitoringForms(monitoringForms) : [],
    },
  };
  formatDimensionOptionsCaching(result.indicators.dimensionOptions, result.indicators.frameworkResults);
  return result;
};

export const getDissaggregations = (indicators: any) => {
  let dissaggregations: any = [];

  if (indicators && indicators.length) {
    indicators.forEach((a: any) => {
      const { dimensions } = a;
      if (dimensions && dimensions.length) {
        dissaggregations.push(dimensions.join(' - '));
      }
    });
  }

  return dissaggregations.map((d: any) => ({
    label: d,
    value: d,
  }));
};

const getformattedIndicators = (indicators: any) => {
  let formattedIndicators: any = [];
  const idSet = new Set();

  if (indicators && indicators.length) {
    indicators.forEach((a: any) => {
      if (!idSet.has(a.indicator.name)) {
        idSet.add(a.indicator.name);

        formattedIndicators.push({
          value: a.id,
          label: a.indicator.name,
        });
      }
    });
  }

  return formattedIndicators;
};

const getFormattedSites = (indicators: any) => {
  let formattedSites: any = [];
  const idSet = new Set();

  if (indicators && indicators.length) {
    indicators.forEach((a: any) => {
      if (a.site && !idSet.has(a.site.id)) {
        idSet.add(a.site.id);

        formattedSites.push({
          value: a.site.id,
          label: a.site.name,
        });
      }
    });
  }

  return formattedSites;
};

export const getFormData = (monitoring: any, user?: any) => {
  let formData: any;
  if (monitoring) {
    const { reporters, reporterSubmissions, indicators, users } = monitoring;
    formData = {
      reporters,
      reporterSubmissions,
      indicators,
      users: user ? users.filter((u: any) => u.user && u.user.id === user.id) : users,
    };
  }

  return formData;
};

const getInitiaTableValues = (monitoring: any, exstData?: any) => {
  let existingData: any = [];

  if (exstData) {
    existingData = exstData;
  }

  const values: any = {
    tableColumns: [],
    tableData: [],
  };

  if (monitoring) {
    // if (monitoring.formReporters.length > 0) {
    //   // formData.reporters.forEach(r => {
    //   //   r.name;
    //   // });
    //   monitoring.formReporters.sort((a: any, b: any) => (a.name && b.name && a.name <= b.name ? -1 : 1));

    //   values.tableColumns.push({
    //     id: 'reporter',
    //     Header: 'Reporter',
    //     accessor: 'reporter.name',
    //   });
    // }

    // Add site column if any indicators have sites
    if (monitoring.indicators.length > 0 && monitoring.indicators.find((i: any) => i.site)) {
      values.tableColumns.push({
        id: 'site',
        Header: 'Site',
        accessor: 'site.name',
      });
    }

    values.tableColumns.push({
      id: 'indicator',
      Header: 'Indicator',
      accessor: 'indicator.name',
    });

    const sortedIndicators = [...monitoring.indicators].sort((a: any, b: any) => {
      if (a.indicator.name < b.indicator.name) return -1;
      for (var i = 0; i < a.dimensions.length || i < b.dimensions.length; i++) {
        const av = i < a.dimensions.length ? a.dimensions[i] : '';
        const ab = i < b.dimensions.length ? b.dimensions[i] : '';
        if (av > ab) return 1;
      }
      return -1;
    });

    const formReporters =
      monitoring.formReporters.length > 0
        ? monitoring.formReporters
        : [
            {
              id: '',
              organisation: {
                id: '',
                funder: null,
                serviceProvider: null,
                institution: null,
              },
            },
          ];

    formReporters.forEach((reporter: any) => {
      sortedIndicators.forEach((formIndicator: any) => {
        const row: any = {
          indicator: formIndicator.indicator,
          id: formIndicator.id,
          reporter: {
            id: reporter.organisation.id,
            name: getOrganisationName(reporter.organisation),
          },
          status: 'DRAFT',
          dimensions: formIndicator.dimensions,
          dimensionValues: {},
          value: '',
          site: formIndicator.site || null,
        };
        var dataIndicators = existingData.filter(
          (d: any) =>
            d.indicatorId === formIndicator.indicator.id &&
            ((reporter.organisation.id === '' && d.dimensionReporter === null) ||
              (d.dimensionReporter && reporter.organisation.id === d.dimensionReporter.id)) &&
            ((!formIndicator.site && !d.site) ||
              (!formIndicator.site && d.site) ||
              (formIndicator.site && d.site && formIndicator.site.id === d.site.id))
        );

        const sortedDimensions = [...formIndicator.indicator.dimensions].sort((a: any, b: any) => a.index - b.index);

        formIndicator.dimensions.forEach((formIndicatorDimension: any, formIndicatorDimensionIndex: any) => {
          const dimension = sortedDimensions[formIndicatorDimensionIndex];
          const key = `d-${dimension.name}`;
          if (values.tableColumns.findIndex((c: any) => c.id === key) < 0) {
            values.tableColumns.push({
              id: key,
              Header: dimension.name,
              accessor: 'dimensionValues.' + key,
            });
          }
          row.dimensionValues[key] = formIndicatorDimension;
          dataIndicators = dataIndicators.filter(
            (d: any) =>
              formIndicatorDimensionIndex < d.dimensions.length &&
              d.dimensions[formIndicatorDimensionIndex] === formIndicatorDimension
          );
        });
        if (dataIndicators.length === 1 && dataIndicators[0].value) {
          row.value = dataIndicators[0].value.toString();
          row.status = dataIndicators[0].submissionStatus;
        }
        values.tableData.push(row);
      });
    });

    values.tableColumns.push({
      id: 'value',
      Header: 'Value',
      accessor: 'value',
    });

    const currentStatuses = new Set(values.tableData.map((row: any) => row.status));
    if (currentStatuses.size > 1) {
      values.tableColumns.push({
        id: 'status',
        Header: 'Status',
        accessor: 'status',
      });
    }
  }

  return values;
};

const formatExitistingMonitoringFormData = (data: any) => {
  const result =
    data &&
    data.map((value: any) => {
      const d: ProgrammeMonitoringFormDataType = {
        id: value.id,
        submissionId: value.submission.id,
        submissionStatus: value.submission.status,
        indicatorId: value.indicator.id,
        periodNumber: value.periodNumber,
        urls: value.submission.urls
          ? value.submission.urls.map((url: any, index: any) => {
              return {
                id: url.id,
                index: index,
                name: url.name,
                link: url.link,
              };
            })
          : [],
        dimensionReporter: value.dimensionReporter
          ? {
              id: value.dimensionReporter.id,
              name: getOrganisationName(value.dimensionReporter),
            }
          : null,
        dimensions: [],
        value: value.value,
        annotation: value.annotation,
        progressStatus: value.progressStatus
          ? {
              value: value.progressStatus,
              label: value.progressStatus,
            }
          : null,
        site: value.site ? value.site : { id: '', name: '' },
      };
      if (value.dimension1) {
        d.dimensions.push(value.dimension1);
        if (value.dimension2) {
          d.dimensions.push(value.dimension2);
          if (value.dimension3) {
            d.dimensions.push(value.dimension3);
            if (value.dimension4) {
              d.dimensions.push(value.dimension4);
              if (value.dimension5) {
                d.dimensions.push(value.dimension5);
              }
            }
          }
        }
      }
      return d;
    });

  return result;
};

const getFilteredTableData = (monitoringForm: any, tableData: any, user: any) => {
  const { users, formReporters } = monitoringForm;
  let currentUser: any = null;
  let userReporter: any = null;
  let newTableData = tableData;

  if (user) {
    currentUser = users.find((u: any) => u.user && u.user.id === user.id);
  }

  if (currentUser && currentUser.reporter && currentUser.reporter.length > 0) {
    userReporter = formReporters.find((r: any) => r.organisation.id === currentUser.reporter[0].id);
  }

  if (!currentUser && formReporters && formReporters.length > 0) {
    const reporterSites = formReporters
      .map((r: any) => r.sites)
      .flat()
      .filter((s: any) => s)
      .map((s: any) => s.id);
    newTableData = newTableData.filter((n: any) => n.site && reporterSites.includes(n.site.id));
  }

  if (currentUser && currentUser.sites && currentUser.sites.length > 0) {
    const userSiteIds = currentUser.sites
      .map((s: any) => (typeof s === 'string' ? s : s && s.id))
      .filter((d: any) => d);
    newTableData = newTableData.filter((n: any) => n.site && userSiteIds.includes(n.site.id));
  } else if (userReporter && userReporter.sites && userReporter.sites.length > 0) {
    const userReporterSiteIds = userReporter.sites
      .map((s: any) => (typeof s === 'string' ? s : s.id))
      .filter((e: any) => e);
    newTableData = newTableData.filter((n: any) => n.site && userReporterSiteIds.includes(n.site.id));
  }

  return newTableData;
};

export const formartMonitoringFormSubmission = (monitoringFormSubmission: any, user?: any) => {
  const { id, periodNumber, monitoringForm, data } = monitoringFormSubmission;

  const existingData = formatExitistingMonitoringFormData(data);
  const { tableColumns, tableData } = getInitiaTableValues(monitoringForm, existingData);

  const result = {
    submissionId: id,
    periodNumber,
    monitoringFormName: monitoringForm.name,
    monitoringFormId: monitoringForm.id,
    indicators: getformattedIndicators(monitoringForm.indicators),
    sites: getFormattedSites(monitoringForm.indicators),
    dissaggregations: getDissaggregations(monitoringForm.indicators),
    formData: getFormData(monitoringForm, user),
    tableColumns,
    tableData: getFilteredTableData(monitoringForm, tableData, user),
  };

  return result;
};

export const formartMonitoringForm = (monitoringForm: any, user: any) => {
  // const { id, periodNumber, monitoringForm, data } = monitoringForm;
  const { tableColumns, tableData } = getInitiaTableValues(monitoringForm);

  const result = {
    submissionId: '',
    periodNumber: monitoringForm.periodNumber,
    monitoringFormName: monitoringForm.name,
    monitoringFormId: monitoringForm.id,
    indicators: getformattedIndicators(monitoringForm.indicators),
    sites: getFormattedSites(monitoringForm.indicators),
    dissaggregations: getDissaggregations(monitoringForm.indicators),
    formData: getFormData(monitoringForm, user),
    tableColumns,
    tableData: getFilteredTableData(monitoringForm, tableData, user),
  };

  return result;
};

export const getUpsertMonitoringFormSubmissionInput = (data: any) => {
  const { submissionId, tableData, monitoringFormId, periodNumber } = data;
  let submissionData: any = [];
  let submissions: any = [];
  const stringToInt = (str: any) => (isNaN(parseInt(str, 10)) ? undefined : parseInt(str, 10));

  if (tableData && tableData.length) {
    tableData.forEach((d: any) => {
      if (d.value) {
        submissionData.push({
          dimensions: d.dimensions,
          indicatorId: d.indicator.id,
          indicatorName: d.indicator.name,
          value: !isNaN(parseInt(d.value)) ? parseInt(d.value) : null,
          siteId: d.site && d.site.id,
        });
      }
    });

    submissions.push({
      id: submissionId,
      status: tableData[0] && tableData[0].status,
      reporterId: tableData[0] && tableData[0].reporter && tableData[0].reporter.id,
      statusDate: new Date(),
      monitoringFormId,
      periodNumber: stringToInt(periodNumber),
      data: submissionData,
    });
  }

  return submissions;
};

const defaultECDEnrolmentData: ECDEnrolmentData = {
  gradeROffered: undefined,
  registeredCapacity: undefined,
  gradeR: undefined,
  year: undefined,
  numberOfMales: undefined,
  numberOfFemales: undefined,
  numberEnrolledWithDisabilities: undefined,
  numberOfAfricanBlack: undefined,
  numberOfColoured: undefined,
  numberOfIndianAsian: undefined,
  numberOfWhite: undefined,
  numberOfOther: undefined,
  ageRange0To18Months: undefined,
  ageRange18MonthsTo3Years: undefined,
  ageRange3To4Years: undefined,
  ageRange4To5Years: undefined,
  ageRange5To6Years: undefined,
  ageRange0To12Months: undefined,
  ageRange1To2Years: undefined,
  ageRange2To3Years: undefined,
  numberOfChildrenEnrolled: undefined,
  gradeRMales: undefined,
  gradeRFemales: undefined,
  numberOfAfricanBlackMales: undefined,
  numberOfAfricanBlackFemales: undefined,
  numberOfColouredMales: undefined,
  numberOfColouredFemales: undefined,
  numberOfIndianAsianMales: undefined,
  numberOfIndianAsianFemales: undefined,
  numberOfWhiteMales: undefined,
  numberOfWhiteFemales: undefined,
  numberOfOtherMales: undefined,
  numberOfOtherFemales: undefined,
  ageRange0To18MonthsMales: undefined,
  ageRange0To18MonthsFemales: undefined,
  ageRange18MonthsTo3YearsMales: undefined,
  ageRange18MonthsTo3YearsFemales: undefined,
  ageRange3To4YearsMales: undefined,
  ageRange3To4YearsFemales: undefined,
  ageRange4To5YearsMales: undefined,
  ageRange4To5YearsFemales: undefined,
  ageRange5To6YearsMales: undefined,
  ageRange5To6YearsFemales: undefined,
  ageRange0To12MonthsMales: undefined,
  ageRange0To12MonthsFemales: undefined,
  ageRange1To2YearsMales: undefined,
  ageRange1To2YearsFemales: undefined,
  ageRange2To3YearsMales: undefined,
  ageRange2To3YearsFemales: undefined,
};

const defaultECDStaffData: ECDStaffData = {
  viewOption: undefined,
  paid: undefined,
  unpad: undefined,
  female: undefined,
  male: undefined,
  year: undefined,
  genderOther: undefined,
  gradeREducators: undefined,
  ecdPractitioners: undefined,
  managerialStaff: undefined,
  supportStuff: undefined,
  fullTime: undefined,
  partTime: undefined,
  accreditedECDSkillsProgram: undefined,
  nqfLevel4to5ECD: undefined,
  nqfLevel6to9ECD: undefined,
  unaccreditedECDSkills: undefined,
  numberOfStaff: undefined,
  fullTimeMales: undefined,
  fullTimeFemales: undefined,
  fullTimeGenderOther: undefined,
  partTimeMales: undefined,
  partTimeFemales: undefined,
  partTimeGenderOther: undefined,
};

const defaultECDFacilityData: ECDFacilityDetail = {
  landowner: undefined,
  landownerOther: '',
  buildingOwner: undefined,
  buildingOwnerOther: '',
  building: undefined,
  buildingOther: '',
  sharedBuilding: undefined,
  numberOfClassrooms: undefined,
  kitchen: undefined,
  separateRoomAsOffice: undefined,
  outdoorPlayArea: undefined,
  fence: undefined,
  gate: undefined,
  outdoorEquipment: [],
  whatHandwashingFacility: [],
  mainSourceOfPowerLighting: undefined,
  mainSourceOfPowerCooking: undefined,
  mainSourceOfPowerHeating: undefined,
  mainSourceOfWater: undefined,
  mainSourceOfWaterOther: '',
  separateToilets: undefined,
  childrenToilets: undefined,
  adultToilets: undefined,
  numToilets: undefined,
  typesOfToilets: [],
};

const getInstitutionYearViewOptionData = (
  data: any[] | undefined,
  thisYear: number,
  viewOption: string,
  defaultData: any
) => {
  let dataThisYear =
    (data &&
      Array.isArray(data) &&
      data.find(x => x.year && x.viewOption && x.year === thisYear && x.viewOption === viewOption)) ||
    (data &&
      Array.isArray(data) &&
      data.find(x => x.year && x.viewOption && x.year === thisYear - 1 && x.viewOption === viewOption)) ||
    defaultData;

  dataThisYear = { ...dataThisYear };
  delete dataThisYear['__typename'];
  delete dataThisYear['id'];
  // delete dataThisYear['year'];
  delete dataThisYear['viewOption'];
  return dataThisYear;
};

const formatContactList = (data: any[] | undefined) => {
  if (data) {
    return data.map(item => {
      const { personnelPosition } = item;
      const contactData = { ...item };
      if (contactData['__typename']) {
        delete contactData['__typename'];
      }
      delete contactData['personnelPosition'];

      return {
        ...contactData,
        personnelPositionId: personnelPosition && { label: personnelPosition.name, value: personnelPosition.id },
      };
    });
  }

  return [];
};

const formatEcdFacilityData = (rest: ECDFacilityDetail) => {
  var result = {
    landowner: rest.landowner && formatSelectValue(rest.landowner),
    landownerOther: rest.landownerOther,
    buildingOwner: rest.buildingOwner && formatSelectValue(rest.buildingOwner),
    buildingOwnerOther: rest.buildingOwnerOther,
    building: rest.building && formatSelectValue(rest.building),
    buildingOther: rest.buildingOther,
    sharedBuilding: rest.sharedBuilding && formatSelectValue(rest.sharedBuilding),
    numberOfClassrooms: rest.numberOfClassrooms,
    kitchen: rest.kitchen && formatSelectValue(rest.kitchen),
    separateRoomAsOffice: rest.separateRoomAsOffice && {
      label: rest.separateRoomAsOffice === true ? 'Yes' : 'No',
      value: rest.separateRoomAsOffice,
    },
    outdoorPlayArea: rest.outdoorPlayArea && {
      label: rest.outdoorPlayArea === true ? 'Yes' : 'No',
      value: rest.outdoorPlayArea,
    },
    fence: rest.fence && {
      label: rest.fence === true ? 'Yes' : 'No',
      value: rest.fence,
    },
    gate: rest.gate && {
      label: rest.gate === true ? 'Yes' : 'No',
      value: rest.gate,
    },
    outdoorEquipment: rest.outdoorEquipment ? rest.outdoorEquipment : [],
    whatHandwashingFacility: rest.whatHandwashingFacility ? rest.whatHandwashingFacility : [],
    mainSourceOfPowerLighting: rest.mainSourceOfPowerLighting,
    mainSourceOfPowerCooking: rest.mainSourceOfPowerCooking,
    mainSourceOfPowerHeating: rest.mainSourceOfPowerHeating,
    mainSourceOfWater: rest.mainSourceOfWater,
    mainSourceOfWaterOther: rest.mainSourceOfWaterOther,
    separateToilets: rest.separateToilets && {
      label: rest.separateToilets === true ? 'Yes' : 'No',
      value: rest.separateToilets,
    },
    childrenToilets: rest.childrenToilets,
    adultToilets: rest.adultToilets,
    numToilets: rest.numToilets,
    typesOfToilets: rest.typesOfToilets ? rest.typesOfToilets : [],
  };
  return result;
};

export const formatInstitutionDataCreate = () => {
  return {
    id: undefined,
    ministryId: undefined,
    typeId: undefined,

    // ecd enrolment data
    ecdEnrolmentDataViewOption: 'current',
    ecdEnrolmentDataCurrent: { ...defaultECDEnrolmentData },
    ecdEnrolmentData10Day: { ...defaultECDEnrolmentData },

    // ecd staff data
    ecdStaffDataViewOption: 'current',
    ecdStaffDataCurrent: { ...defaultECDStaffData },
    ecdStaffData10Day: { ...defaultECDStaffData },

    // ecd facility data
    ecdFacilityDetail: { ...defaultECDFacilityData },
  };
};

export const formatInstitutionData = (institution: Institution) => {
  const formattedKeys = [
    'addOnIds',
    'areaOfficeId',
    'circuitId',
    'countryId',
    'districtId',
    'districtMunicipalityId',
    'fundingClassificationId',
    'localMunicipalityId',
    'ministryId',
    'phaseId',
    'planId',
    'provinceId',
    'registrationTypeId',
    'schoolTypeId',
    'sectorId',
    'socialServicesProfessionalId',
    'specializationId',
    'sspSupervisorId',
    'typeId',
  ];
  const shouldBeFormatted = Object.keys(institution).every(key => !formattedKeys.includes(key));

  if (!shouldBeFormatted) return institution;

  const ecdEmploymentStimulus = institution.receivedEcdEsrf
    ? { label: 'Received ECD-ESRF', value: 'Received ECD-ESRF' }
    : institution.appliedForEcdEsrf
    ? { label: 'Applied for ECD-ESRF', value: 'Applied for ECD-ESRF' }
    : undefined;

  const thisYear = new Date().getFullYear();
  let nutritionThisYear =
    institution.nutrition && Array.isArray(institution.nutrition)
      ? institution.nutrition.find(x => x.year === thisYear)
      : undefined;
  if (nutritionThisYear === undefined)
    nutritionThisYear =
      institution.nutrition && Array.isArray(institution.nutrition)
        ? institution.nutrition.find(x => x.year === thisYear - 1)
        : undefined;

  let ecdDetailsThisYear =
    institution.ecdFinanceDetails && Array.isArray(institution.ecdFinanceDetails)
      ? institution.ecdFinanceDetails.find(x => x.year === thisYear)
      : undefined;

  if (ecdDetailsThisYear === undefined) {
    ecdDetailsThisYear =
      institution.ecdFinanceDetails && Array.isArray(institution.ecdFinanceDetails)
        ? institution.ecdFinanceDetails.find(x => x.year === thisYear - 1)
        : undefined;
  }

  let ecdEnrolmentDataCurrentThisYear = getInstitutionYearViewOptionData(
    institution.ecdEnrolmentData,
    thisYear,
    'current',
    defaultECDEnrolmentData
  );
  let ecdEnrolmentData10DayThisYear = getInstitutionYearViewOptionData(
    institution.ecdEnrolmentData,
    thisYear,
    '10day',
    defaultECDEnrolmentData
  );
  let ecdStaffDataCurrentThisYear = getInstitutionYearViewOptionData(
    institution.ecdStaffData,
    thisYear,
    'current',
    defaultECDStaffData
  );
  let ecdStaffData10DayThisYear = getInstitutionYearViewOptionData(
    institution.ecdStaffData,
    thisYear,
    '10day',
    defaultECDStaffData
  );

  return {
    id: institution.id,
    addressee: institution.addressee || undefined,
    ageRange0To18Months: institution.ageRange0To18Months || undefined,
    ageRange18MonthsTo3Years: institution.ageRange18MonthsTo3Years || undefined,
    ageRange3To4Years: institution.ageRange3To4Years || undefined,
    ageRange4To5Years: institution.ageRange4To5Years || undefined,
    ageRange5To6Years: institution.ageRange5To6Years || undefined,
    allocation: institution.allocation || undefined,
    ecdEmploymentStimulus,
    appliedForEcdEsrf: institution.appliedForEcdEsrf || undefined,
    alternativeContactPersonFullName:
      (institution.alternativeContactPerson &&
        `${institution.alternativeContactPerson.name} ${institution.alternativeContactPerson.lastName}`) ||
      undefined,
    alternativeContactPersonContactNumber:
      (institution.alternativeContactPerson && institution.alternativeContactPerson.contactNumber) || undefined,
    alternativeContactPersonEmail:
      (institution.alternativeContactPerson && institution.alternativeContactPerson.emailAddress) || undefined,
    alternativeContactPersonDesignation:
      (institution.alternativeContactPerson && institution.alternativeContactPerson.designation) || undefined,
    classification: institution.classification || undefined,
    contactPersonFullName:
      (institution.contactPerson && `${institution.contactPerson.name} ${institution.contactPerson.lastName}`) ||
      undefined,
    contactPersonContactNumber: (institution.contactPerson && institution.contactPerson.contactNumber) || undefined,
    contactPersonEmail: (institution.contactPerson && institution.contactPerson.emailAddress) || undefined,
    contactPersonDesignation: (institution.contactPerson && institution.contactPerson.designation) || undefined,
    certificateNumber: institution.certificateNumber || undefined,
    componentNumber: institution.componentNumber || undefined,
    dateEcdEsrfReceived: institution.dateEcdEsrfReceived ? new Date(institution.dateEcdEsrfReceived) : undefined,
    duplicateAction: institution.duplicateAction && formatSelectValue(institution.duplicateAction),
    duplicateReference: institution.duplicateReference || undefined,
    ecdRegistrationProgress: institution.ecdRegistrationProgress || undefined,
    ecdServiceType: institution.ecdServiceType && formatSelectValue(institution.ecdServiceType),
    educatorNumber: institution.educatorNumber || undefined,
    emisNumber: institution.emisNumber || undefined,
    schoolEmmisNumber: institution.schoolEmmisNumber || undefined,
    schoolInstitutionId: undefined,
    exDept: institution.exDept || undefined,
    examCentre: institution.examCentre || undefined,
    examNumber: institution.examNumber || undefined,
    expiryDate: institution.expiryDate ? new Date(institution.expiryDate) : undefined,
    facsimile: institution.facsimile || undefined,
    gisAltitude: institution.gisAltitude || undefined,
    gisLatitude: institution.gisLatitude || undefined,
    gisLongitude: institution.gisLongitude || undefined,
    gisSource: institution.gisSource || undefined,
    gradeROffered: institution.gradeROffered || undefined,
    identifiedBy: institution.identifiedBy && formatSelectValue(institution.identifiedBy),
    identifiedDate: institution.identifiedDate ? new Date(institution.identifiedDate) : undefined,
    isArchived: institution.isArchived || undefined,
    issueDate: institution.issueDate ? new Date(institution.issueDate) : undefined,
    learnerNumber: institution.learnerNumber || undefined,
    locality: institution.locality && formatSelectValue(institution.locality),
    name: institution.name || undefined,
    nas: institution.nas || undefined,
    nearestPointOfInterest: institution.nearestPointOfInterest && formatSelectValue(institution.nearestPointOfInterest),
    newEmisNumber: institution.newEmisNumber || undefined,
    newOrRenewal: institution.newOrRenewal && formatSelectValue(institution.newOrRenewal),
    noFeeSchool: institution.noFeeSchool || undefined,
    nodalArea: institution.nodalArea || undefined,
    npoNumber: institution.npoNumber || undefined,
    numberOfChildrenEnrolled: institution.numberOfChildrenEnrolled || undefined,
    numberOfStaff: institution.numberOfStaff || undefined,
    officeContactNumber: institution.officeContactNumber || undefined,
    officeEmailAddress: institution.officeEmailAddress || undefined,
    organisationName: institution.organisationName || undefined,
    networkName: institution.networkName || undefined,
    openBoardingSchool: institution.openBoardingSchool || undefined,
    ownerBuild: institution.ownerBuild || undefined,
    ownerLand: institution.ownerLand || undefined,
    ownership: institution.ownership || undefined,
    paypointNumber: institution.paypointNumber || undefined,
    pcrLevel: institution.pcrLevel || undefined,
    physicalAddress: institution.physicalAddress || undefined,
    pointOfInterest: institution.pointOfInterest || undefined,
    postalAddress: institution.postalAddress || undefined,
    postalCode: institution.postalCode || undefined,
    privateOrNpo: institution.privateOrNpo && formatSelectValue(institution.privateOrNpo),
    progCertificateExpiryDate: institution.progCertificateExpiryDate
      ? new Date(institution.progCertificateExpiryDate)
      : undefined,
    progCertificateIssueDate: institution.progCertificateIssueDate
      ? new Date(institution.progCertificateIssueDate)
      : undefined,
    progCertificateNumber: institution.progCertificateNumber || undefined,
    progLevel: institution.progLevel || undefined,
    progStatusId: institution.progStatus && formatValues(institution.progStatus),
    provinceCD: institution.provinceCD || undefined,
    quintile: institution.quintile || undefined,
    reasonForDuplicateAction: institution.reasonForDuplicateAction || undefined,
    receivedEcdEsrf: institution.receivedEcdEsrf || undefined,
    region: institution.region || undefined,
    registrationDate: institution.registrationDate ? new Date(institution.registrationDate) : undefined,
    registrationOfficeIds:
      institution.registrationOffice && formatOrganisationArrayValues(institution.registrationOffice),
    registrationStatusId: institution.registrationStatus && formatValues(institution.registrationStatus),
    registrationNumber: institution.registrationNumber || undefined,
    section21: institution.section21 || undefined,
    section21Function: institution.section21Function || undefined,
    specialization: institution.specialization || undefined,
    status: institution.status || undefined,
    streetAddress: institution.streetAddress || undefined,
    streetNo: institution.streetNo || undefined,
    suburb: institution.suburb || undefined,
    townCity: institution.townCity || undefined,
    townshipVillage: institution.townshipVillage || undefined,
    streetBuilding: institution.streetBuilding || undefined,
    area: institution.area || undefined,
    // altitude: institution.altitude || undefined,
    verified: institution.verified || undefined,
    validated: institution.validated || undefined,
    wardID: institution.wardID || undefined,
    wardLabel: institution.wardLabel || undefined,
    yearOfReceipt: institution.yearOfReceipt || undefined,
    addOnIds: institution.organisationUsersAddOns && formatArrayValues(institution.organisationUsersAddOns),
    areaOfficeId: institution.areaOffice && formatValues(institution.areaOffice),
    circuitId: institution.circuit && formatValues(institution.circuit),
    countryId: institution.country && formatValues(institution.country),
    districtId: institution.district && formatValues(institution.district),
    districtMunicipalityId: institution.districtMunicipality && formatValues(institution.districtMunicipality),
    fundingClassificationId: institution.fundingClassification && formatValues(institution.fundingClassification),
    localMunicipalityId: institution.localMunicipality
      ? formatSelectValue({
          label: institution.localMunicipality.localMunicipalityShorthand,
          value: institution.localMunicipality.id,
        })
      : undefined,
    ministryId: institution.ministry && formatValues(institution.ministry),
    phaseId: institution.phase && formatValues(institution.phase),
    planId: institution.organisationUsersPlan && formatValues(institution.organisationUsersPlan),
    provinceId: institution.province && formatValues(institution.province),
    registrationTypeId: institution.registrationType && formatValues(institution.registrationType),
    schoolTypeId: institution.schoolType && formatValues(institution.schoolType),
    sectorId: institution.sector && formatValues(institution.sector),
    socialServicesProfessionalId:
      institution.socialServicesProfessional && formatUserValues(institution.socialServicesProfessional),
    specializationId: institution.specializationGroup && formatValues(institution.specializationGroup),
    sspSupervisorId: institution.sspSupervisor && formatUserValues(institution.sspSupervisor),
    typeId: institution.type && formatValues(institution.type),
    languageOfLearning: institution.languageOfLearning ? institution.languageOfLearning.split(',') : undefined,
    closingTime: institution.closingTime ? formatTime(institution.closingTime) : undefined,
    openingTime: institution.openingTime ? formatTime(institution.openingTime) : undefined,
    afterCareForSchoolGoing: institution.afterCareForSchoolGoing && {
      label: institution.afterCareForSchoolGoing ? 'Yes' : 'No',
      value: institution.afterCareForSchoolGoing,
    },
    primarySchoolName: institution.primarySchoolName || undefined,
    primarySchoolEmis: institution.primarySchoolEmis || undefined,
    isEcdServicePrimarySchoolBased: institution.isEcdServicePrimarySchoolBased && {
      label: institution.isEcdServicePrimarySchoolBased ? 'Yes' : 'No',
      value: institution.isEcdServicePrimarySchoolBased,
    },
    hasHolidays: institution.hasHolidays && {
      label: institution.hasHolidays ? 'Yes' : 'No',
      value: institution.hasHolidays,
    },
    numberOfDaysServiceOperate: institution.numberOfDaysServiceOperate || undefined,
    yearEcdStartedOperating: institution.yearEcdStartedOperating && institution.yearEcdStartedOperating,
    legalEntity: institution.legalEntity && formatSelectValue(institution.legalEntity),
    registeredAsNpo: institution.registeredAsNpo && {
      label: institution.registeredAsNpo === 1 ? 'Yes' : institution.registeredAsNpo === 0 ? 'No' : 'Dont Know',
      value: institution.registeredAsNpo,
    },
    communityTypeId: institution.communityType && formatValues(institution.communityType),
    suburbId: institution.suburbRelation && formatValues(institution.suburbRelation),

    // nutrition
    foodProvider:
      nutritionThisYear && nutritionThisYear.foodProvider ? nutritionThisYear.foodProvider.split(',') : undefined,
    foodProviderOther: nutritionThisYear ? nutritionThisYear.foodProviderOther : undefined,
    mealsProvided:
      nutritionThisYear && nutritionThisYear.mealsProvided ? nutritionThisYear.mealsProvided.split(',') : undefined,
    nutritionYear: nutritionThisYear ? nutritionThisYear.year : undefined,

    // ecd finance details
    fundedThroughConditionalGrant: ecdDetailsThisYear ? ecdDetailsThisYear.fundedThroughConditionalGrant : undefined,
    fundedThroughEquitableShare: ecdDetailsThisYear ? ecdDetailsThisYear.fundedThroughEquitableShare : undefined,
    fundedThroughTopUpSubsidy: ecdDetailsThisYear ? ecdDetailsThisYear.fundedThroughTopUpSubsidy : undefined,
    monthlyFeeRangeHighest: ecdDetailsThisYear ? ecdDetailsThisYear.monthlyFeeRangeHighest : undefined,
    monthlyFeeRangeLowest: ecdDetailsThisYear ? ecdDetailsThisYear.monthlyFeeRangeLowest : undefined,
    assessedForMaintenanceGrant: ecdDetailsThisYear ? ecdDetailsThisYear.assessedForMaintenanceGrant : undefined,
    receivedMaintenanceGrant: ecdDetailsThisYear ? ecdDetailsThisYear.receivedMaintenanceGrant : undefined,
    feesCharged: ecdDetailsThisYear && {
      label: ecdDetailsThisYear.feesCharged === true ? 'Yes' : 'No',
      value: ecdDetailsThisYear.feesCharged,
    },
    numChildrenSubsidisedCG: ecdDetailsThisYear ? ecdDetailsThisYear.numChildrenSubsidisedCG : undefined,
    numberOfChildrenFunded: ecdDetailsThisYear ? ecdDetailsThisYear.numChildrenSubsidisedES : undefined,
    numChildrenSubsidisedTU: ecdDetailsThisYear ? ecdDetailsThisYear.numChildrenSubsidisedTU : undefined,
    mainFundingSource: ecdDetailsThisYear ? ecdDetailsThisYear.mainFundingSource : undefined,
    bankAccount: ecdDetailsThisYear ? ecdDetailsThisYear.bankAccount : undefined,
    receivesDBESubsidy: ecdDetailsThisYear ? ecdDetailsThisYear.receivesDBESubsidy : undefined,
    monthlyESBudgetTotal: ecdDetailsThisYear ? ecdDetailsThisYear.monthlyESBudgetTotal : undefined,
    monthlyCGBudgetTotal: ecdDetailsThisYear ? ecdDetailsThisYear.monthlyCGBudgetTotal : undefined,
    monthlyTopUpSubsidyTotal: ecdDetailsThisYear ? ecdDetailsThisYear.monthlyTopUpSubsidyTotal : undefined,
    infrastructureGrantBudget: ecdDetailsThisYear ? ecdDetailsThisYear.infrastructureGrantBudget : undefined,
    ecdFinanceDetailsYear: ecdDetailsThisYear ? ecdDetailsThisYear.year : undefined,

    // ecd enrolment data
    ecdEnrolmentDataViewOption: 'current',
    ecdEnrolmentDataCurrent: ecdEnrolmentDataCurrentThisYear,
    ecdEnrolmentData10Day: ecdEnrolmentData10DayThisYear,

    // ecd staff data
    ecdStaffDataViewOption: 'current',
    ecdStaffDataCurrent: ecdStaffDataCurrentThisYear,
    ecdStaffData10Day: ecdStaffData10DayThisYear,

    // ecd facility data
    ecdFacilityDetail: institution.ecdFacilityDetail
      ? formatEcdFacilityData(institution.ecdFacilityDetail)
      : { ...defaultECDFacilityData },
    contactList: institution.organisation ? formatContactList(institution.organisation.contactList) : [],
  };
};

export const formatFormInstitutionToDBInstitution = (institution: any) => {
  const {
    alternativeContactPersonContactNumber,
    alternativeContactPersonDesignation,
    alternativeContactPersonEmail,
    alternativeContactPersonFullName,
    contactPersonContactNumber,
    contactPersonDesignation,
    contactPersonEmail,
    contactPersonFullName,

    addOnIds,
    areaOfficeId,
    circuitId,
    countryId,
    districtId,
    districtMunicipalityId,
    fundingClassificationId,
    localMunicipalityId,
    ministryId,
    phaseId,
    planId,
    progStatusId,
    provinceId,
    registrationOfficeIds,
    registrationStatusId,
    registrationTypeId,
    schoolTypeId,
    sectorId,
    socialServicesProfessionalId,
    specializationId,
    sspSupervisorId,
    typeId,
    communityTypeId,
    suburbId,
    ecdEmploymentStimulus,

    ...rest
  } = institution;

  return {
    addressee: rest.addressee,
    ageRange0To18Months: rest.ageRange0To18Months,
    ageRange18MonthsTo3Years: rest.ageRange18MonthsTo3Years,
    ageRange3To4Years: rest.ageRange3To4Years,
    ageRange4To5Years: rest.ageRange4To5Years,
    ageRange5To6Years: rest.ageRange5To6Years,
    allocation: rest.allocation,
    appliedForEcdEsrf: Boolean(ecdEmploymentStimulus && ecdEmploymentStimulus.value === 'Applied for ECD-ESRF'),
    assessedForMaintenanceGrant: Boolean(rest.assessedForMaintenanceGrant),
    certificateNumber: rest.certificateNumber,
    classification: rest.classification,
    componentNumber: rest.componentNumber,
    dateEcdEsrfReceived: rest.dateEcdEsrfReceived,
    duplicateAction: rest.duplicateAction && rest.duplicateAction.value,
    duplicateReference: rest.duplicateReference,
    ecdRegistrationProgress: rest.ecdRegistrationProgress,
    ecdServiceType: rest.ecdServiceType && rest.ecdServiceType.value,
    educatorNumber: rest.educatorNumber,
    emisNumber: rest.emisNumber,
    exDept: rest.exDept,
    examCentre: rest.examCentre,
    examNumber: rest.examNumber,
    expiryDate: rest.expiryDate,
    facsimile: rest.facsimile,
    fundedThroughConditionalGrant: rest.fundedThroughConditionalGrant,
    fundedThroughEquitableShare: rest.fundedThroughEquitableShare,
    gisAltitude: rest.gisAltitude,
    gisLatitude: rest.gisLatitude,
    gisLongitude: rest.gisLongitude,
    gisSource: rest.gisSource,
    gradeROffered: rest.gradeROffered,
    id: rest.id,
    identifiedBy: rest.identifiedBy && rest.identifiedBy.value,
    identifiedDate: rest.identifiedDate,
    isArchived: rest.isArchived,
    issueDate: rest.issueDate,
    learnerNumber: rest.learnerNumber,
    locality: rest.locality && rest.locality.value,
    monthlyFeeRangeHighest: rest.monthlyFeeRangeHighest,
    monthlyFeeRangeLowest: rest.monthlyFeeRangeLowest,
    name: rest.name,
    nas: rest.nas,
    nearestPointOfInterest: rest.nearestPointOfInterest && rest.nearestPointOfInterest.value,
    newEmisNumber: rest.newEmisNumber,
    newOrRenewal: rest.newOrRenewal && rest.newOrRenewal.value,
    noFeeSchool: rest.noFeeSchool,
    nodalArea: rest.nodalArea,
    npoNumber: rest.npoNumber,
    numberOfChildrenEnrolled: rest.numberOfChildrenEnrolled,
    numberOfChildrenFunded: rest.numberOfChildrenFunded,
    numberOfStaff: rest.numberOfStaff,
    officeContactNumber: rest.officeContactNumber,
    officeEmailAddress: rest.officeEmailAddress,
    organisationName: rest.organisationName,
    networkName: rest.networkName,
    openBoardingSchool: rest.openBoardingSchool,
    ownerBuild: rest.ownerBuild,
    ownerLand: rest.ownerLand,
    ownership: rest.ownership,
    paypointNumber: rest.paypointNumber,
    pcrLevel: rest.pcrLevel,
    physicalAddress: rest.physicalAddress,
    pointOfInterest: rest.pointOfInterest,
    postalAddress: rest.postalAddress,
    postalCode: rest.postalCode,
    privateOrNpo: rest.privateOrNpo && rest.privateOrNpo.value,
    progCertificateExpiryDate: rest.progCertificateExpiryDate,
    progCertificateIssueDate: rest.progCertificateIssueDate,
    progCertificateNumber: rest.progCertificateNumber,
    progLevel: rest.progLevel,
    provinceCD: rest.provinceCD,
    quintile: rest.quintile,
    reasonForDuplicateAction: rest.reasonForDuplicateAction,
    receivedEcdEsrf: Boolean(ecdEmploymentStimulus && ecdEmploymentStimulus.value === 'Received ECD-ESRF'),
    receivedMaintenanceGrant: rest.receivedMaintenanceGrant,
    region: rest.region,
    registrationDate: rest.registrationDate,
    registrationNumber: rest.registrationNumber,
    section21: rest.section21,
    section21Function: rest.section21Function,
    specialization: rest.specialization,
    status: rest.status,
    streetAddress: rest.streetAddress,
    streetNo: rest.streetNo,
    suburb: rest.suburb,
    townCity: rest.townCity,
    townshipVillage: rest.townshipVillage,
    streetBuilding: rest.streetBuilding,
    area: rest.area,
    // altitude: rest.altitude,
    verified: rest.verified,
    validated: rest.validated,
    wardID: rest.wardID,
    wardLabel: rest.wardLabel,
    year: rest.year,
    yearOfReceipt: rest.yearOfReceipt,
    languageOfLearning: rest.languageOfLearning && rest.languageOfLearning.value,
    closingTime: rest.closingTime,
    openingTime: rest.openingTime,
    afterCareForSchoolGoing: rest.afterCareForSchoolGoing && rest.afterCareForSchoolGoing.value,
    primarySchoolName: rest.primarySchoolName,
    primarySchoolEmis: rest.primarySchoolEmis,
    isEcdServicePrimarySchoolBased: rest.isEcdServicePrimarySchoolBased && rest.isEcdServicePrimarySchoolBased.value,
    hasHolidays: rest.hasHolidays && rest.hasHolidays.value,
    numberOfDaysServiceOperate: rest.numberOfDaysServiceOperate,
    yearEcdStartedOperating: rest.yearEcdStartedOperating && rest.yearEcdStartedOperating.value,
    legalEntity: rest.legalEntity && rest.legalEntity.value,
    registeredAsNpo: rest.registeredAsNpo && parseInt(rest.registeredAsNpo.value),

    alternativeContactPerson: {
      contactNumber: alternativeContactPersonContactNumber,
      designation: alternativeContactPersonDesignation,
      emailAddress: alternativeContactPersonEmail,
      firstName: alternativeContactPersonFullName && alternativeContactPersonFullName.split(' ')[0],
      lastName: alternativeContactPersonFullName && alternativeContactPersonFullName.split(' ')[1],
    },
    areaOffice: areaOfficeId && { id: areaOfficeId.value, name: areaOfficeId.label },
    circuit: circuitId && { id: circuitId.value, name: circuitId.label },
    contactPerson: {
      contactNumber: contactPersonContactNumber,
      designation: contactPersonDesignation,
      emailAddress: contactPersonEmail,
      firstName: contactPersonFullName && contactPersonFullName.split(' ')[0],
      lastName: contactPersonFullName && contactPersonFullName.split(' ')[1],
    },
    country: countryId && { id: countryId.value, name: countryId.label },
    district: districtId && { id: districtId.value, name: districtId.label },
    districtMunicipality: districtMunicipalityId && {
      id: districtMunicipalityId.value,
      name: districtMunicipalityId.label,
    },
    fundingClassification: fundingClassificationId && {
      id: fundingClassificationId.value,
      name: fundingClassificationId.label,
    },
    localMunicipality: localMunicipalityId && { id: localMunicipalityId.value, name: localMunicipalityId.label },
    ministry: ministryId && { id: ministryId.value, name: ministryId.label },
    organisationUsersAddOns: Boolean(addOnIds && addOnIds.length)
      ? addOnIds.map((item: any) => ({ id: item.value, name: item.label }))
      : [],
    organisationUsersPlan: planId && { id: planId.value, name: planId.label },
    phase: phaseId && { id: phaseId.value, name: phaseId.label },
    progStatus: progStatusId && { id: progStatusId.value, name: progStatusId.label },
    province: provinceId && { id: provinceId.value, name: provinceId.label },
    registrationOffice: Boolean(registrationOfficeIds && registrationOfficeIds.length)
      ? formatOrganisationArrayValues(registrationOfficeIds)
      : [],
    registrationStatus: registrationStatusId && { id: registrationStatusId.value, name: registrationStatusId.label },
    registrationType: registrationTypeId && { id: registrationTypeId.value, name: registrationTypeId.label },
    schoolType: schoolTypeId && { id: schoolTypeId.value, name: schoolTypeId.label },
    sector: sectorId && { id: sectorId.value, name: sectorId.label },
    socialServicesProfessional: socialServicesProfessionalId && {
      id: socialServicesProfessionalId.value,
      firstName: socialServicesProfessionalId.label.split(' ')[0],
      lastName: socialServicesProfessionalId.label.split(' ')[1],
    },
    communityType: communityTypeId && { id: communityTypeId.value, name: communityTypeId.label },
    suburbRelation: suburbId && { id: suburbId.value, name: suburbId.label },
    specializationGroup: specializationId && { id: specializationId.value, name: specializationId.label },
    sspSupervisor: sspSupervisorId && {
      id: sspSupervisorId.value,
      firstName: sspSupervisorId.label.split(' ')[0],
      lastName: sspSupervisorId.label.split(' ')[1],
    },
    type: typeId && { id: typeId.value, name: typeId.label },
    // nutrition
    nutrition: [
      {
        year: new Date().getFullYear(),
        foodProvider: rest.foodProvider && rest.foodProvider.map((x: any) => x.value).toString(),
        foodProviderOther: rest.foodProviderOther,
        mealsProvided: rest.mealsProvided && rest.mealsProvided.map((x: any) => x.value).toString(),
      },
    ],
    // ecd finance detail
    ecdFinanceDetails: [
      {
        year: new Date().getFullYear(),
        fundedThroughConditionalGrant: rest.fundedThroughConditionalGrant,
        fundedThroughEquitableShare: rest.fundedThroughEquitableShare,
        fundedThroughTopUpSubsidy: rest.fundedThroughTopUpSubsidy,
        monthlyFeeRangeHighest: rest.monthlyFeeRangeHighest,
        monthlyFeeRangeLowest: rest.monthlyFeeRangeLowest,
        assessedForMaintenanceGrant: rest.assessedForMaintenanceGrant,
        receivedMaintenanceGrant: rest.receivedMaintenanceGrant,
        feesCharged: rest.feesCharged,
        numChildrenSubsidisedCG: rest.numChildrenSubsidisedCG,
        numChildrenSubsidisedES: rest.numberOfChildrenFunded, //numChildrenSubsidisedES
        numChildrenSubsidisedTU: rest.numChildrenSubsidisedTU,
        mainFundingSource: rest.mainFundingSource && rest.mainFundingSource.value,
        bankAccount: rest.bankAccount,
        receivesDBESubsidy: rest.receivesDBESubsidy,
        monthlyESBudgetTotal: rest.monthlyESBudgetTotal,
        monthlyCGBudgetTotal: rest.monthlyCGBudgetTotal,
        monthlyTopUpSubsidyTotal: rest.monthlyTopUpSubsidyTotal,
        infrastructureGrantBudget: rest.infrastructureGrantBudget,
      },
    ],
    ecdEnrollmentData: [
      {
        ...rest.ecdEnrolmentDataCurrent,
        year: new Date().getFullYear(),
        viewOption: 'current',
      },
      {
        ...rest.ecdEnrolmentData10Day,
        year: new Date().getFullYear(),
        viewOption: '10day',
      },
    ],
    ecdFacilityData: {
      landowner: rest.ecdFacilityDetail.landowner && rest.ecdFacilityDetail.landowner,
      landownerOther: rest.ecdFacilityDetail.landownerOther,
      buildingOwner: rest.ecdFacilityDetail.buildingOwner && rest.ecdFacilityDetail.buildingOwner,
      buildingOwnerOther: rest.ecdFacilityDetail.buildingOwnerOther,
      building: rest.ecdFacilityDetail.building && rest.ecdFacilityDetail.building,
      buildingOther: rest.ecdFacilityDetail.buildingOther,
      sharedBuilding: rest.ecdFacilityDetail.sharedBuilding && rest.ecdFacilityDetail.sharedBuilding,
      numberOfClassrooms: rest.ecdFacilityDetail.numberOfClassrooms,
      kitchen: rest.ecdFacilityDetail.kitchen,
      separateRoomAsOffice: rest.ecdFacilityDetail.separateRoomAsOffice,
      outdoorPlayArea: rest.ecdFacilityDetail.outdoorPlayArea,
      fence: rest.ecdFacilityDetail.fence,
      gate: rest.ecdFacilityDetail.gate,
      outdoorEquipment:
        rest.ecdFacilityDetail.outdoorEquipment &&
        rest.ecdFacilityDetail.outdoorEquipment.length &&
        rest.ecdFacilityDetail.outdoorEquipment.map((x: any) => x.value).toString(),
      whatHandwashingFacility:
        rest.ecdFacilityDetail.whatHandwashingFacility && rest.ecdFacilityDetail.whatHandwashingFacility,
      mainSourceOfPowerLighting: rest.ecdFacilityDetail.mainSourceOfPowerLighting,
      mainSourceOfPowerCooking: rest.ecdFacilityDetail.mainSourceOfPowerCooking,
      mainSourceOfPowerHeating: rest.ecdFacilityDetail.mainSourceOfPowerHeating,
      mainSourceOfWater: rest.ecdFacilityDetail.mainSourceOfWater,
      mainSourceOfWaterOther: rest.ecdFacilityDetail.mainSourceOfWaterOther,
      separateToilets: rest.ecdFacilityDetail.separateToilets,
      childrenToilets: rest.ecdFacilityDetail.childrenToilets,
      adultToilets: rest.ecdFacilityDetail.adultToilets,
      numToilets: rest.ecdFacilityDetail.numToilets,
      typesOfToilets:
        rest.ecdFacilityDetail.typesOfToilets &&
        rest.ecdFacilityDetail.typesOfToilets.map((x: any) => x.value).toString(),
    },
    ecdStaffData: rest.ecdStaffData,
  };
};

const formatProvidedDocuments = (documents: ProvidedDocument[]) => {
  if (!documents || !documents.length) return [];

  return documents.map(({ id, document, provided }) => ({
    ecdProvidedDocumentId: id,
    ecdRequiredDocumentId: document && document.id,
    provided,
  }));
};

const formatFrameworkAnswers = (answers: FrameworkAnswer[]) => {
  const response = {};

  if (!answers || !answers.length) return response;

  answers.forEach(({ question, answer }) => {
    if (question && question.name) {
      response[question.name] = answer;
    }
  });

  answers.forEach(({ question, answer }) => {
    if (question && question.tag) {
      response['tag:' + question.tag] = answer;
    }
  });

  return response;
};

export const formatECDRegistrationData = (ecdRegistration: any, institution?: any | undefined) => {
  const formattedKeys = ['stepApplication', 'stepFramework', 'stepAssessment', 'stepApproval'];

  const shouldBeFormatted = Object.keys(ecdRegistration).every(key => !formattedKeys.includes(key));

  if (!shouldBeFormatted) return ecdRegistration;

  const {
    afterCareComments,
    applicationCollectionType,
    applicationNotes,
    applicationSubmissionType,
    approvalStatus,
    approvedBy,
    assessEcdFrameworkOnline,
    assessmentNotes,
    assignee,
    associatedEcdInstitution,
    certificateNumber,
    dateAssigned,
    dateAttended,
    dateCertificateReceivedFromDistrict,
    dateCollected,
    dateEHPReportReceived,
    dateCertificateDispensed,
    dateFileCompilationSubmitted,
    dateIdentified,
    dateInvited,
    dateOfDSDSiteVisit,
    dateOfEHPSiteVisit,
    dateProgCertificateDispensed,
    dateProgCertificateReceivedFromDistrict,
    dateSubmissionCompleted,
    dateSubmitted,
    expiryDate,
    frameworkAnswers,
    generalSafetyComments,
    id,
    identifiedBy,
    issueDate,
    modifiedBy,
    newOrRenewal,
    numberOfChildrenPresent,
    numberOfStaffPresent,
    otherAssessmentDelayReasons,
    pcrLevel,
    progCertificateExpiryDate,
    progCertificateIssueDate,
    progCertificateNumber,
    progLevel,
    providedDocuments,
    programmeAssessmentOutcome,
    progRegistrationOutcome,
    reasonForAssessmentDelay,
    reasonForCertificationDelay,
    reasonForProgCertificationDelay,
    reasonForRejection,
    progReasonForRejection,
    reasonForWithdrawal,
    registrationOutcome,
    registrationType,
    siteAssessmentOutcome,
    submissionNumber,
    submitted,
    transportComments,
  } = ecdRegistration;

  const institutionDistrictMunicipality =
    institution && institution.districtMunicipality ? `${institution.districtMunicipality.name || ''}` : '';
  const institutionProvince = institution && institution.province ? `${institution.province.name || ''}` : '';
  const institutionAddress = `${(institution && institution.physicalAddress) ||
    (associatedEcdInstitution && associatedEcdInstitution.physicalAddress) ||
    ''}${
    (institution && institution.postalAddress) || (associatedEcdInstitution && associatedEcdInstitution.postalAddress)
      ? `, ${(institution && institution.postalAddress) ||
          (associatedEcdInstitution && associatedEcdInstitution.postalAddress)}`
      : ''
  }${
    (institution && institution.postalCode) || (associatedEcdInstitution && associatedEcdInstitution.postalCode)
      ? `, ${(institution && institution.postalCode) ||
          (associatedEcdInstitution && associatedEcdInstitution.postalCode)}`
      : ''
  }`;

  const assigneeName = assignee ? `${assignee.firstName} ${assignee.lastName}` : '';
  const modifierName = modifiedBy ? `${modifiedBy.firstName} ${modifiedBy.lastName}` : '';

  return {
    id,
    submitted,
    stepApplication: {
      identifiedBy: identifiedBy ? formatSelectValue(identifiedBy) : '',
      dateIdentified: dateIdentified ? new Date(dateIdentified) : undefined,
      institutionId: institution ? institution.id : associatedEcdInstitution ? associatedEcdInstitution.id : undefined,
      sspSupervisorId:
        (((institution && institution.sspSupervisor) ||
          (associatedEcdInstitution && associatedEcdInstitution.sspSupervisor)) &&
          formatUserValues(
            (institution && institution.sspSupervisor) ||
              (associatedEcdInstitution && associatedEcdInstitution.sspSupervisor)
          )) ||
        '',
      socialServicesProfessionalId:
        (((institution && institution.socialServicesProfessional) ||
          (associatedEcdInstitution && associatedEcdInstitution.socialServicesProfessional)) &&
          formatUserValues(
            (institution && institution.socialServicesProfessional) ||
              (associatedEcdInstitution && associatedEcdInstitution.socialServicesProfessional)
          )) ||
        '',
      dateAssigned: dateAssigned ? new Date(dateAssigned) : undefined,
      newOrRenewal: newOrRenewal && formatSelectValue(newOrRenewal),
      registrationTypeId: registrationType ? formatValues(registrationType) : '',
      applicationCollectionType: applicationCollectionType ? formatSelectValue(applicationCollectionType) : '',
      dateInvited: dateInvited ? new Date(dateInvited) : undefined,
      dateAttended: dateAttended ? new Date(dateAttended) : undefined,
      dateCollected: dateCollected ? new Date(dateCollected) : undefined,
      applicationSubmissionType: applicationSubmissionType ? formatSelectValue(applicationSubmissionType) : '',
      dateSubmitted: dateSubmitted ? new Date(dateSubmitted) : undefined,
      submissionNumber: submissionNumber || '',
      providedDocuments: formatProvidedDocuments(providedDocuments),
      dateSubmissionCompleted: dateSubmissionCompleted ? new Date(dateSubmissionCompleted) : undefined,
      assessEcdFrameworkOnline,
      applicationNotes: applicationNotes || '',
    },
    stepFramework: {
      afterCareComments: afterCareComments || '',
      dateOfDSDSiteVisit: dateOfDSDSiteVisit ? new Date(dateOfDSDSiteVisit) : null,
      exportPDFTitle: {
        name: institution ? institution.name : associatedEcdInstitution ? associatedEcdInstitution.name : undefined,
        address: institutionAddress,
        province:
          institutionProvince ||
          (associatedEcdInstitution && associatedEcdInstitution.province && associatedEcdInstitution.province.name),
        municipality:
          institutionDistrictMunicipality ||
          (associatedEcdInstitution &&
            associatedEcdInstitution.districtMunicipality &&
            associatedEcdInstitution.districtMunicipality.name),
        dateVisit: dateOfDSDSiteVisit ? new Date(dateOfDSDSiteVisit) : null,
        assignee: assigneeName || modifierName ? assigneeName || modifierName : '',
        localMunicipality:
          institution && institution.localMunicipality ? institution.localMunicipality.localMunicipalityShorthand : '',
        locality: institution && institution.locality ? institution.locality : '',
        ward: institution && institution.wardID ? institution.wardID : '',
        siteAssessmentOutcome: siteAssessmentOutcome ? siteAssessmentOutcome : '',
        programmeAssessmentOutcome: programmeAssessmentOutcome ? programmeAssessmentOutcome : '',
      },
      frameworkAnswers: formatFrameworkAnswers(frameworkAnswers),
      generalSafetyComments: generalSafetyComments || '',
      numberOfChildrenEnrolled:
        institution && institution.numberOfChildrenEnrolled
          ? institution.numberOfChildrenEnrolled
          : associatedEcdInstitution && associatedEcdInstitution.numberOfChildrenEnrolled
          ? associatedEcdInstitution.numberOfChildrenEnrolled
          : 0,
      numberOfChildrenPresent: numberOfChildrenPresent || 0,
      numberOfStaffPresent: numberOfStaffPresent || 0,
      programmeAssessmentOutcome: programmeAssessmentOutcome ? formatSelectValue(programmeAssessmentOutcome) : null,
      registrationOutcome: registrationOutcome ? formatSelectValue(registrationOutcome) : null,
      registrationTypeId: registrationType ? formatValues(registrationType) : '',
      siteAssessmentOutcome: siteAssessmentOutcome ? formatSelectValue(siteAssessmentOutcome) : null,
      transportComments: transportComments || '',
    },
    stepAssessment: {
      approvedBy: approvedBy
        ? `${approvedBy.firstName || ''}${approvedBy.firstName ? ' ' : ''}${approvedBy.lastName || ''}`
        : undefined,
      assessEcdFrameworkOnline,
      assessmentNotes: assessmentNotes || undefined,
      certificateExpiryDate: expiryDate ? new Date(expiryDate) : null,
      certificateIssueDate: issueDate ? new Date(issueDate) : null,
      certificateNumber: certificateNumber,
      dateCertificateReceivedFromDistrict: dateCertificateReceivedFromDistrict
        ? new Date(dateCertificateReceivedFromDistrict)
        : null,
      dateFileCompilationSubmitted: dateFileCompilationSubmitted ? new Date(dateFileCompilationSubmitted) : null,
      dateOfDSDSiteVisit: dateOfDSDSiteVisit ? new Date(dateOfDSDSiteVisit) : null,
      dateOfEHPSiteVisit: dateOfEHPSiteVisit ? new Date(dateOfEHPSiteVisit) : null,
      dateEHPReportReceived: dateEHPReportReceived ? new Date(dateEHPReportReceived) : null,
      dateCertificateDispensed: dateCertificateDispensed ? new Date(dateCertificateDispensed) : null,
      dateProgCertificateDispensed: dateProgCertificateDispensed ? new Date(dateProgCertificateDispensed) : null,
      dateProgCertificateReceivedFromDistrict: dateProgCertificateReceivedFromDistrict
        ? new Date(dateProgCertificateReceivedFromDistrict)
        : null,
      otherAssessmentDelayReasons: otherAssessmentDelayReasons,
      pcrLevel: pcrLevel ? formatSelectValue(pcrLevel) : null,
      progCertificateExpiryDate: progCertificateExpiryDate ? new Date(progCertificateExpiryDate) : null,
      progCertificateIssueDate: progCertificateIssueDate ? new Date(progCertificateIssueDate) : null,
      progCertificateNumber: progCertificateNumber,
      progLevel: progLevel ? formatSelectValue(progLevel) : null,
      progRegistrationOutcome: progRegistrationOutcome ? formatSelectValue(progRegistrationOutcome) : null,
      programmeAssessmentOutcome: programmeAssessmentOutcome ? formatSelectValue(programmeAssessmentOutcome) : null,
      reasonForAssessmentDelay: reasonForAssessmentDelay ? formatSelectValue(reasonForAssessmentDelay) : null,
      reasonForCertificationDelay: reasonForCertificationDelay,
      reasonForProgCertificationDelay: reasonForProgCertificationDelay,
      reasonForRejection,
      progReasonForRejection,
      registrationOutcome: registrationOutcome ? formatSelectValue(registrationOutcome) : null,
      registrationTypeId: registrationType ? formatValues(registrationType) : '',
      siteAssessmentOutcome: siteAssessmentOutcome ? formatSelectValue(siteAssessmentOutcome) : null,
    },
    stepApproval: {
      approvalStatus: approvalStatus
        ? formatSelectValue({ label: formatString(approvalStatus, true), value: approvalStatus })
        : null,
      reasonForRejection: reasonForRejection || '',
      progReasonForRejection: progReasonForRejection || '',
      reasonForWithdrawal: reasonForWithdrawal || '',
      registrationOutcome: registrationOutcome ? formatSelectValue(registrationOutcome) : null,
      progRegistrationOutcome: progRegistrationOutcome ? formatSelectValue(progRegistrationOutcome) : null,
    },
  };
};

export const formatFormRegistrationToDBRegistration = (ecdRegistration: any) => {
  const {
    id,
    submitted,
    stepApplication: {
      identifiedBy,
      dateIdentified,
      institutionId,
      sspSupervisorId,
      socialServicesProfessionalId,
      dateAssigned,
      newOrRenewal,
      applicationCollectionType,
      dateInvited,
      dateAttended,
      dateCollected,
      applicationSubmissionType,
      dateSubmitted,
      submissionNumber,
      providedDocuments,
      dateSubmissionCompleted,
      applicationNotes,
    },
    stepFramework: {
      afterCareComments,
      exportPDFTitle,
      frameworkAnswers,
      generalSafetyComments,
      numberOfChildrenEnrolled,
      numberOfChildrenPresent,
      numberOfStaffPresent,
      transportComments,
    },
    stepAssessment: {
      approvedBy,
      assessmentNotes,
      certificateExpiryDate,
      certificateIssueDate,
      certificateNumber,
      dateCertificateReceivedFromDistrict,
      dateFileCompilationSubmitted,
      dateOfEHPSiteVisit,
      dateEHPReportReceived,
      dateCertificateDispensed,
      dateProgCertificateDispensed,
      dateProgCertificateReceivedFromDistrict,
      otherAssessmentDelayReasons,
      pcrLevel,
      progCertificateExpiryDate,
      progCertificateIssueDate,
      progCertificateNumber,
      progLevel,
      progRegistrationOutcome,
      reasonForAssessmentDelay,
      reasonForCertificationDelay,
      reasonForProgCertificationDelay,
      reasonForRejection,
      progReasonForRejection,
    },
    stepApproval: { approvalStatus },
  } = ecdRegistration;

  const regTypeId =
    ecdRegistration.stepApplication.registrationTypeId ||
    ecdRegistration.stepFramework.registrationTypeId ||
    ecdRegistration.stepAssessment.registrationTypeId;

  const documents = providedDocuments
    ? providedDocuments.map((a: any) => {
        const { ecdProvidedDocumentId, ecdRequiredDocumentId, provided } = a;

        return {
          ecdProvidedDocumentId: ecdProvidedDocumentId,
          ecdRequiredDocumentId: ecdRequiredDocumentId,
          provided: provided,
        };
      })
    : [];

  return {
    afterCareComments,
    applicationCollectionType,
    applicationNotes,
    applicationSubmissionType,
    approvalStatus,
    approvedBy,
    assessEcdFrameworkOnline: ecdRegistration.stepApplication.assessEcdFrameworkOnline,
    assessmentNotes,
    assignee: exportPDFTitle && exportPDFTitle.assignee.split(' ')[0].concat(exportPDFTitle.assignee.split(' ')[1]),
    associatedEcdInstitution: {
      id: institutionId,
      districtMunicipality: exportPDFTitle && { name: exportPDFTitle.municipality },
      locality: exportPDFTitle && exportPDFTitle.locality,
      localMunicipality: exportPDFTitle && { localMunicipalityShorthand: exportPDFTitle.localMunicipality },
      name: exportPDFTitle && exportPDFTitle.name,
      numberOfChildrenEnrolled,
      physicalAddress: exportPDFTitle && exportPDFTitle.address.split(',')[0],
      postalAddress: exportPDFTitle && exportPDFTitle.address.split(',')[1],
      postalCode: exportPDFTitle && exportPDFTitle.address.split(',')[2],
      province: exportPDFTitle && { name: exportPDFTitle.province },
      socialServicesProfessional: socialServicesProfessionalId && { id: socialServicesProfessionalId.value },
      sspSupervisor: sspSupervisorId && { id: sspSupervisorId.value },
      wardID: exportPDFTitle && exportPDFTitle.ward,
    },
    certificateNumber,
    dateAssigned,
    dateAttended,
    dateCertificateReceivedFromDistrict,
    dateCollected,
    dateEHPReportReceived,
    dateCertificateDispensed,
    dateFileCompilationSubmitted,
    dateIdentified,
    dateInvited,
    dateOfDSDSiteVisit:
      ecdRegistration.stepFramework.dateOfDSDSiteVisit || ecdRegistration.stepAssessment.dateOfDSDSiteVisit,
    dateOfEHPSiteVisit,
    dateSubmissionCompleted,
    dateSubmitted,
    expiryDate: certificateExpiryDate,
    frameworkAnswers,
    generalSafetyComments,
    id,
    identifiedBy: identifiedBy && identifiedBy.value,
    issueDate: certificateIssueDate,
    modifiedBy:
      exportPDFTitle &&
      exportPDFTitle &&
      exportPDFTitle.assignee.split(' ')[0].concat(exportPDFTitle.assignee.split(' ')[1]),
    newOrRenewal: newOrRenewal && newOrRenewal.value,
    numberOfChildrenPresent,
    numberOfStaffPresent,
    otherAssessmentDelayReasons,
    providedDocuments: documents,
    programmeAssessmentOutcome:
      (ecdRegistration.stepFramework.programmeAssessmentOutcome &&
        ecdRegistration.stepFramework.programmeAssessmentOutcome.value) ||
      (ecdRegistration.stepAssessment.programmeAssessmentOutcome &&
        ecdRegistration.stepAssessment.programmeAssessmentOutcome.value),
    reasonForAssessmentDelay,
    reasonForCertificationDelay,
    reasonForRejection,
    progReasonForRejection,
    registrationOutcome:
      (ecdRegistration.stepFramework.registrationOutcome && ecdRegistration.stepFramework.registrationOutcome.value) ||
      (ecdRegistration.stepAssessment.registrationOutcome && ecdRegistration.stepAssessment.registrationOutcome.value),
    registrationType: regTypeId && {
      id: regTypeId.value,
      name: regTypeId.label,
    },
    siteAssessmentOutcome:
      (ecdRegistration.stepFramework.siteAssessmentOutcome &&
        ecdRegistration.stepFramework.siteAssessmentOutcome.value) ||
      (ecdRegistration.stepAssessment.siteAssessmentOutcome &&
        ecdRegistration.stepAssessment.siteAssessmentOutcome.value),
    submissionNumber,
    submitted,
    transportComments,
    pcrLevel: pcrLevel && pcrLevel.value,
    progCertificateExpiryDate,
    progCertificateIssueDate,
    progCertificateNumber,
    progLevel: progLevel && progLevel.value,
    progRegistrationOutcome: progRegistrationOutcome && progRegistrationOutcome.value,
    dateProgCertificateDispensed,
    dateProgCertificateReceivedFromDistrict,
    reasonForProgCertificationDelay,
  };
};

export const formatSubjectValues = (array: { subjectGradeReference: string; id: string }[]) => {
  return array && Boolean(Array.isArray(array))
    ? array.map(value =>
        formatSelectValue({
          label: value.subjectGradeReference,
          value: value.id,
        })
      )
    : undefined;
};

export const formatfileName = (fileName: string) => {
  const date = format(new Date(), 'yyyyMMdd');
  const randomString = Math.random()
    .toString(36)
    .substring(2, 7);
  const cleanfileName = fileName.toLowerCase().replace(/[^a-z0-9]/g, '-');
  const newfileName = `images/${date}-${randomString}-${cleanfileName}`;
  return newfileName.substring(0, 60);
};

/**
 * This will format data returned by the graphql
 * query 'getEnum' (the enumValues field) ready to be supplied to
 * a react-select.
 * @param {array} data - This is an array of data
 * we would like to convert for a select box to
 * understand
 * @param {boolean} caps - This is a boolean to
 * either make the first letter of each word a
 * capital letter or not.
 */
export function formatDropDownEnums(data: any, caps: boolean) {
  try {
    const values: any[] = (data.enumValues || []).map((value: any) => {
      if (value.name !== 'SUPERADMIN' && value.name !== 'FIELD_AGENT') {
        return {
          label: formatString(value.name, caps),
          value: value.name,
        };
      }
      return undefined;
    });
    return values.filter(value => !!value);
  } catch (error) {
    return [];
  }
}

export const mergeCounts = (arr: MonthlyCountData[]) => {
  let counts = arr.reduce((prev, curr) => {
    let count = prev.get(curr.month) || 0;
    prev.set(curr.month, curr.count + count);
    return prev;
  }, new Map());

  // then, map your counts object back to an array
  // @ts-ignore
  let reducedObjArr = [...counts].map(([key, value]) => {
    return { month: key, count: value };
  });
  return reducedObjArr;
};

/**
 * This function serves to strip the label property
 * out of the form's Select component.
 * @param {object} data - This is the object containing
 * all the form labels that we'd like to clean up.
 */
export function stripSelectLabels(data: object): object {
  try {
    Object.keys(data).forEach((item: any) => {
      let val = data[item];

      if (Boolean(Array.isArray(val))) {
        data[item] = val.map((obj: any) => {
          if (Boolean(Array.isArray(obj))) {
            return stripSelectLabels(obj);
          }

          if ('label' in obj && 'value' in obj) {
            return obj.label;
          }

          return stripSelectLabels(obj);
        });
      }

      if (val && typeof val === 'object' && 'label' in val && 'value' in val) {
        const selectLabel = val.label;
        data[item] = selectLabel;
      }
    });
    return data;
  } catch (error) {
    return {};
  }
}

/**
 * This function serves to strip the value property
 * out of the form's Select component.
 * @param {object} data - This is the object containing
 * all the form values that we'd like to clean up.
 */
export function stripSelectValues(data: object): object {
  try {
    Object.keys(data).forEach((item: string) => {
      if (item.startsWith('react-select')) {
        delete data[item];
      }

      let val = data[item];

      if (Boolean(Array.isArray(val))) {
        if (item === 'frameworkAnswers') {
          return data[item];
        }

        data[item] = val.map((obj: any) => {
          if (Boolean(Array.isArray(obj))) {
            return stripSelectValues(obj);
          }

          if (obj && typeof obj === 'object' && 'label' in obj && 'value' in obj) {
            return obj.value;
          }

          return stripSelectValues(obj);
        });
      }

      if (val && typeof val === 'object' && 'label' in val && 'value' in val) {
        const selectValue = val.value;
        data[item] = selectValue;
      }
    });
    return data;
  } catch (error) {
    console.error('stripSelectValues: %s', error);
    return {};
  }
}

export const insertString = (a: string, b: string, at: string) => {
  let position = a.indexOf(at);

  if (position !== -1) {
    return a.substr(0, position) + b + a.substr(position);
  }

  return 'substring not found';
};

/**
 * Turns an object into an array
 * @param object
 */
export const getArrayFromObject = (object?: object): any[] => {
  if (!object) {
    return [];
  }

  const arrayFromObject = [];

  for (let item in object) {
    if (Object.keys(object[item]).length > 0) {
      arrayFromObject.push({
        ...object[item],
      });
    }
  }

  return arrayFromObject;
};

export const filterTypeNameFromObject = (obj: any, key: string) => {
  for (let i in obj) {
    if (!obj.hasOwnProperty(i)) continue;
    if (i === key) {
      delete obj[i];
    } else if (typeof obj[i] == 'object' && obj[i] !== null) {
      filterTypeNameFromObject(obj[i], key);
    }
  }
  return obj;
};

export const formatDeclarationData = (data: Declaration) => {
  const { portal, organisation } = data;

  return {
    id: data.id,
    published: data.published,
    text: data.text,
    title: data.title,
    portalId: portal ? formatValues(portal) : null,
    organisationId: organisation ? formatValues(organisation) : null,
  };
};

export const getIDFromOption = (obj: Option | undefined): string => {
  if (obj === undefined || obj === null) return '';
  return obj.value;
};

export const getIDArrayFromOptionArray = (obj: Option[] | undefined): string[] => {
  if (obj === undefined || obj === null) return [];
  return obj.map((v: Option) => v.value);
};

export const formatTemplateData = (data: Template) => {
  const {
    id,
    administration,
    createdBy,
    funderRespondents,
    institutionRespondents,
    modifiedBy,
    owner,
    questions,
    serviceProviderRespondents,
    tags,
    type,
    jsonData,
  } = data;

  return {
    id: id,
    allFunderRespondents: data.allFunderRespondents || false,
    allInstitutionRespondents: data.allInstitutionRespondents || false,
    allServiceProviderRespondents: data.allServiceProviderRespondents || false,
    description: data.description || undefined,
    editable: data.editable || false,
    name: data.name || undefined,
    published: data.published || undefined,
    submissions: data.submissions || undefined,
    version: data.version || undefined,
    administrationId: (administration && formatValues(administration)) || null,
    createdById: (createdBy && formatUserValues(createdBy)) || '',
    funderRespondentIds: (funderRespondents && formatArrayValues(funderRespondents)) || '',
    institutionRespondentIds: (institutionRespondents && formatArrayValues(institutionRespondents)) || '',
    modifiedById: (modifiedBy && formatUserValues(modifiedBy)) || '',
    ownerIds: (owner && formatOrganisationArrayValues(owner)) || '',
    questions: filterTypeNameFromObject(JSON.parse(JSON.stringify(questions)), '__typename'),
    serviceProviderRespondentIds: (serviceProviderRespondents && formatArrayValues(serviceProviderRespondents)) || '',
    tagIds: (tags && formatArrayValues(tags)) || '',
    typeId: type ? formatValues(type) : null,
    jsonData: jsonData ? jsonData : undefined,
  };
};

export const formatCohortData = (data: Cohort) => {
  const { id, beneficiariesCount, description, institutionsCount, intervention, name, peopleCount, status } = data;

  return {
    id,
    beneficiariesCount: beneficiariesCount || undefined,
    description: description || undefined,
    institutionsCount: institutionsCount || undefined,
    interventionId: intervention ? formatValues(intervention) : undefined,
    name: name || undefined,
    peopleCount: peopleCount || undefined,
    status: status ? formatSelectValue(status) : undefined,
  };
};

export const formatPortfolio = (data: any): Portfolio => {
  return {
    id: data.id,
    name: data.name,
    owner: data.owner
      ? {
          id: data.owner.id,
          funder: data.owner.funder
            ? {
                id: data.owner.funder.id,
                name: data.owner.funder.name,
              }
            : undefined,
          serviceProvider: data.owner.serviceProvider
            ? {
                id: data.owner.serviceProvider.id,
                name: data.owner.serviceProvider.name,
              }
            : undefined,
          institution: data.owner.institution
            ? {
                id: data.owner.institution.id,
                name: data.owner.institution.name,
              }
            : undefined,
        }
      : undefined,
    creator: data.creator
      ? {
          id: data.creator.id,
          firstName: data.creator.firstName,
          lastName: data.creator.lastName,
          email: data.creator.email,
        }
      : undefined,
    assignedTo: data.assignedTo
      ? data.assignedTo.map((v: any) => {
          return {
            id: v.id,
            firstName: v.firstName,
            lastName: v.lastName,
            email: v.email,
          };
        })
      : undefined,
    programmes: data.programmes
      ? data.programmes.map((v: any) => {
          return {
            id: v.id,
            name: v.name,
          };
        })
      : undefined,
  };
};

const getAnswer = (value: any) => {
  try {
    const answer = JSON.parse(value.replace(/'/g, '"'));
    return answer;
  } catch (error) {
    return value;
  }
};

const formatSubmissionAnswers = (answers: WorkspaceFormSubmissionAnswer[]) => {
  const response = {};

  if (!answers || !answers.length) return response;

  answers.map(({ question, answer }) => (response[question.name] = getAnswer(answer)));
  answers.map(({ question, answer }) => (response['tag:' + question.tag] = getAnswer(answer)));

  return response;
};

export const formatSubmissionData = (submission: WorkspaceFormSubmission) => {
  const { id, workspaceForm, data, userRespondent, status } = submission;

  return {
    id: id,
    name: (workspaceForm && workspaceForm.name) || undefined,
    published: (workspaceForm && workspaceForm.published) || undefined,
    jsonData:
      workspaceForm && workspaceForm.template && workspaceForm.template.jsonData
        ? workspaceForm.template.jsonData
        : undefined,
    answers: formatSubmissionAnswers(data),
    userRespondent,
    status,
  };
};

export const convertStringFieldsToDate = (item: any, fields: any) => {
  for (const [key, value] of Object.entries(item)) {
    if (Object.keys(fields).includes(key) && value !== null && value !== undefined) {
      if (typeof value === 'string') {
        item[key] = value ? new Date(value) : null;
      } else {
        convertStringFieldsToDate(value, fields[key]);
      }
    }
  }
};

const defaultActivitySessionDetailsData: ActivitySessionDetails = {
  plannedDate: undefined,
  actualDate: undefined,
  plannedDuration: undefined,
  actualDuration: undefined,
  name: undefined,
  description: undefined,
  additionalNotes: undefined,
  fileIds: undefined,
  sessionStatus: 'DRAFT',
};

export const defaultActivitySessionAttendeesData: ActivitySessionAttendees = {
  attendees: undefined,
};

export const formatActivitySessionData = (
  session: ActivitySession | undefined,
  activityId: string | undefined,
  sessionActivity: Activity | undefined,
  targetId: string | undefined
) => {
  if (!session) {
    return {
      id: undefined,
      stepDetails: {
        ...defaultActivitySessionDetailsData,
        activityId,
        plannedDuration: targetId
          ? sessionActivity && sessionActivity.activityTargets.filter(t => t.id === targetId)[0].plannedDuration
          : 0,
        sessionActivity: sessionActivity || {},
      },
      stepAttendees: { ...defaultActivitySessionAttendeesData },
    };
  }

  const {
    id,
    activity,
    activityTarget,
    actualDate,
    actualDuration,
    plannedDuration,
    additionalNotes,
    description,
    files,
    institutions,
    name,
    numberOfAttendees,
    plannedDate,
    sessionStatus,
    stakeholderRole,
    attendeeHeadCount,
    service,
    site,
  } = session;

  const formattedAttendeeHeadCount = attendeeHeadCount
    ? attendeeHeadCount.map((a: any) => {
        const { attendeeGroup, institution, stakeHolderGroup, targetGroup, __typename, ...rest } = a;

        return {
          ...rest,
          stakeHolderGroupId: stakeHolderGroup && stakeHolderGroup.id,
          targetGroupId: targetGroup && targetGroup.id,
        };
      })
    : [];

  return {
    id,
    stepDetails: {
      ...defaultActivitySessionDetailsData,
      activityId,
      actualDate: actualDate ? new Date(actualDate) : undefined,
      actualDuration: actualDuration || undefined,
      additionalNotes: additionalNotes || undefined,
      description: description || undefined,
      fileIds: files && files.length > 0 ? files[0].id : undefined,
      name: name || undefined,
      plannedDate: plannedDate ? new Date(plannedDate) : undefined,
      plannedDuration: (activityTarget && activityTarget.plannedDuration) || plannedDuration || undefined,
      sessionActivity: activity || sessionActivity,
      sessionActivityTarget: activityTarget || undefined,
      sessionStatus: sessionStatus || undefined,
      serviceId: service ? formatValues(service) : undefined,
      siteId: site ? formatValues(site) : undefined,
    },
    stepAttendees: {
      ...defaultActivitySessionAttendeesData,
      numberOfAttendees: numberOfAttendees || 0,
      institutionIds: institutions ? formatArrayValues(institutions) : undefined,
      stakeholderRoleId: stakeholderRole ? formatValues(stakeholderRole) : undefined,
      attendeeHeadCount: formattedAttendeeHeadCount,
    },
  };
};

export const getPeopleProfileFormTypeDefault = (): PeopleProfileFormType => {
  return {
    id: '',
    firstName: '',
    lastName: '',
    name: '',
    nationalID: '',
    beneficiaryType: '',
    branch: '',
    contactNumber: '',
    dateExited: null,
    dateOfBirth: null,
    dateStarted: null,
    designation: '',
    disability: undefined,
    emailAddress: '',
    emisNumber: undefined,
    ictSkillId: undefined,
    leadEducator: false,
    localMunicipalityId: undefined,
    multiGrade: false,
    otherRole: undefined,
    ovc: undefined,
    persalNumber: '',
    postLevel: undefined,
    prePrimaryExperience: false,
    regionId: undefined,
    year: undefined,
    yearsInGrade: null,
    yearsInPhase: null,
    yearsOfExperience: null,
    communityTypeId: undefined,
    countryId: undefined,
    districtId: undefined,
    educatorLevelId: undefined,
    genderId: undefined,
    highestQualificationId: undefined,
    homeLanguageId: undefined,
    interventionRoleId: undefined,
    natureOfEmploymentId: undefined,
    personnelPositionId: undefined,
    personnelPositionOther: '',
    phaseId: undefined,
    populationGroupId: undefined,
    programmeId: undefined,
    provinceId: undefined,
    districtMunicipalityId: undefined,
    terminationId: undefined,
    renumerationSourceId: undefined,
    roleId: undefined,
    trackingId: '',
    typeId: undefined,
    gradeIds: [],
    subjectIds: [],
    careGiverName: '',
    careGiverGender: undefined,
    careGiverSurname: '',
    careGiverNationalId: '',
    careGiverMaritalStatus: undefined,
    careGiverContactNumber: '',
    careGiverEmailAddress: '',
    careGiverPhysicalAddress: '',
    careGiverOccupation: undefined,
    careGiverEmployer: '',
    careGiverType: undefined,
    careGiverRelationship: undefined,

    additionalInstitution: false,
    primaryInstitutionId: undefined,
    institutionIds: [],
    associatedInstitutions: [],
    associatedOrganisations: [],
    organisation: '',

    immunizationCard: undefined,
    receivingSocialGrant: undefined,
    accountPayer: undefined,
    childResidesWithThisCaregive: undefined,
    grantType: undefined,
    birthCertificateOrId: true,
    appliedForBirthCertificateOrId: false,
    citizenshipId: undefined,
    reasonForLackOfBirthCertificateOrId: '',
    age: undefined,
    proofOfIncomeFile: undefined,
    roadToHealthCardFile: undefined,
    monthlyIncomeRange: undefined,
    monthlyIncome: undefined,
    incomeSourceDocument: undefined,
    identificationType: undefined,
    caregiverIdentificationType: undefined,
    caregiverCitizenshipId: undefined,
    caregiverPassport: '',
    ageRange: undefined,
    employmentStatus: undefined,
    passport: '',
    reasonForLackOfRoadToHealthCard: '',
    specialNeeds: '',
    studyPermit: undefined,
    studyPermitNumber: '',
    reasonExited: '',
    disabilityTypeIds: undefined,
    caregiverList: [],
    privateToOrganisation: false,
    caseId: '',
    householdId: '',
    householdCode: '',
    profileImage: undefined,
    physicalAddress: '',
    streetNo: '',
    streetName: '',
    townshipVillageSuburb: '',
    townCity: '',
    postalCode: '',
    nextOfKinName: '',
    nextOfKinSurname: '',
    nextOfKinContactNumber: '',
    nextOfKinAlternateContactNumber: '',
    linkedToCommunity: 'YES',
    linkedToOrganisation: 'YES',
  };
};

export const formatPeopleProfileFormType = (data: any): PeopleProfileFormType => {
  const formattedKeys = [
    'localMunicipalityId',
    'ictSkillId',
    'countryId',
    'districtId',
    'educatorLevelId',
    'districtMunicipalityId',
    'genderId',
    'highestQualificationId',
    'interventionRoleId',
    'natureOfEmploymentId',
    'personnelPositionId',
    'phaseId',
    'provinceId',
    'typeId',
    'primaryInstitutionId',
    'institutionIds',
    'citizenshipId',
    'disabilityTypeIds',
  ];
  const shouldBeFormatted = Object.keys(data).every(key => !formattedKeys.includes(key));

  if (!shouldBeFormatted) return data;

  const gradeIds = data.grades ? data.grades.map((g: any) => ({ value: g.id, label: g.value })) : [];
  const subjectIds = formatArrayValues(data.subjects);

  return {
    id: data.id,
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    name: data.name || '',
    nationalID: data.nationalID || '',
    beneficiaryType: data.beneficiaryType || '',
    branch: data.branch || '',
    contactNumber: data.contactNumber,
    dateExited: data.dateExited ? new Date(data.dateExited) : null,
    dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth) : null,
    dateStarted: data.dateStarted ? new Date(data.dateStarted) : null,
    designation: data.designation || '',
    disability: formatValues(data.disability),
    emailAddress: data.emailAddress || '',
    emisNumber: data.emisNumber ? data.emisNumber : undefined,
    ictSkillId: formatValues(data.ictSkill),
    leadEducator: formatBoolean(data.leadEducator, false),
    localMunicipalityId: data.localMunicipality
      ? formatValues({
          id: data.localMunicipality.id,
          name: data.localMunicipality.localMunicipality,
        })
      : undefined,
    districtMunicipalityId: data.districtMunicipality ? formatValues(data.districtMunicipality) : undefined,
    multiGrade: formatBoolean(data.multiGrade, false),
    otherRole: data.otherRole ? formatValues({ id: data.otherRole, name: data.otherRole }) : undefined,
    ovc: formatValues(data.ovc),
    persalNumber: data.persalNumber || '',
    postLevel: formatNumberValues(data.postLevel),
    prePrimaryExperience: formatBoolean(data.prePrimaryExperience, false),
    regionId: formatValues(data.region),
    year: data.year ? formatValues(data.year) : undefined,
    yearsInGrade: data.yearsInGrade,
    yearsInPhase: data.yearsInPhase,
    yearsOfExperience: data.yearsOfExperience,
    communityTypeId: formatValues(data.communityType),
    countryId: formatValues(data.country),
    districtId: data.district ? data.district.id : undefined,
    educatorLevelId: formatValues(data.educatorLevel),
    genderId: formatValues(data.gender),
    highestQualificationId: formatValues(data.highestQualification),
    homeLanguageId: formatValues(data.homeLanguage),
    interventionRoleId: formatValues(data.interventionRole),
    natureOfEmploymentId: formatValues(data.natureOfEmployment),
    personnelPositionId: formatValues(data.personnelPosition),
    personnelPositionOther: data.personnelPositionOther || '',
    phaseId: formatValues(data.phase),
    populationGroupId: formatValues(data.populationGroup),
    programmeId: formatValues(data.programme),
    provinceId: formatValues(data.province),
    terminationId: formatValues(data.reasonForTermination),
    trackingId: data.trackingId || '',
    renumerationSourceId: formatValues(data.renumerationSourceId),
    roleId: formatValues(data.role),
    typeId: formatValues(data.type),
    gradeIds: gradeIds ? gradeIds : [],
    subjectIds: subjectIds ? subjectIds : [],
    careGiverName: data.careGiverName || '',
    careGiverGender: formatValues(data.careGiverGender),
    careGiverSurname: data.careGiverSurname || '',
    careGiverNationalId: data.careGiverNationalId || '',
    careGiverMaritalStatus: formatValues(data.careGiverMaritalStatus),
    careGiverContactNumber: data.careGiverContactNumber || '',
    careGiverEmailAddress: data.careGiverEmailAddress || '',
    careGiverPhysicalAddress: data.careGiverPhysicalAddress || '',
    careGiverOccupation: formatValues(data.careGiverOccupation),
    careGiverEmployer: data.careGiverEmployer || '',
    careGiverType: formatValues(data.careGiverType),
    careGiverRelationship: formatValues(data.careGiverRelationship),

    additionalInstitution: formatBoolean(data.additionalInstitution, false),
    primaryInstitutionId: formatValues(data.primaryInstitution),
    institutionIds: formatArrayValues(data.institutions) || [],
    associatedInstitutions: data.associatedInstitutions
      ? data.associatedInstitutions.map((x: any) => formatPeopleProfileInstitutionFormType(x))
      : [],
    associatedOrganisations: data.associatedOrganisations
      ? data.associatedOrganisations.map((x: any) => formatPeopleProfileOrganisationFormType(x))
      : [],
    organisation: data.organisation || '',

    immunizationCard: formatValues(data.immunizationCard),
    receivingSocialGrant: formatValues(data.receivingSocialGrant),
    accountPayer: formatValues(data.accountPayer),
    childResidesWithThisCaregive: formatValues(data.childResidesWithThisCaregive),
    grantType: formatValues(data.grantType),
    birthCertificateOrId: formatBoolean(data.birthCertificateOrId, false),
    appliedForBirthCertificateOrId: formatBoolean(data.appliedForBirthCertificateOrId, false),
    citizenshipId: formatValues(data.citizenship),
    reasonForLackOfBirthCertificateOrId: data.reasonForLackOfBirthCertificateOrId || '',
    age: data.age,
    proofOfIncomeFile: data.proofOfIncomeFile ? data.proofOfIncomeFile.id : undefined,
    roadToHealthCardFile: data.roadToHealthCardFile ? data.roadToHealthCardFile.id : undefined,
    monthlyIncomeRange: formatValues(data.monthlyIncomeRange),
    monthlyIncome: data.monthlyIncome,
    incomeSourceDocument: formatValues(data.incomeSourceDocument),
    identificationType: formatValues(data.identificationType),
    caregiverIdentificationType: formatValues(data.caregiverIdentificationType),
    caregiverCitizenshipId: formatValues(data.caregiverCitizenship),
    caregiverPassport: data.caregiverPassport || '',
    ageRange: formatValues(data.ageRange),
    employmentStatus: formatValues(data.employmentStatus),
    passport: data.passport || '',
    reasonForLackOfRoadToHealthCard: data.reasonForLackOfRoadToHealthCard || '',
    specialNeeds: data.specialNeeds || '',
    studyPermit: formatValues(data.studyPermit),
    studyPermitNumber: data.studyPermitNumber || '',
    reasonExited: data.reasonExited || '',
    disabilityTypeIds: data.disabilityTypes && formatArrayValues(data.disabilityTypes),
    caregiverList: data.caregiverList ? data.caregiverList.map((x: any) => formatCaregiverList(x)) : [],
    privateToOrganisation: formatBoolean(data.privateToOrganisation, false),
    caseId: data.caseId || '',
    householdId: data.householdId || '',
    householdCode: data.householdCode || '',
    profileImage: data.profileImage ? data.profileImage.id : undefined,
    physicalAddress: data.physicalAddress || '',
    streetNo: data.streetNo || '',
    streetName: data.streetName || '',
    townshipVillageSuburb: data.townshipVillageSuburb || '',
    townCity: data.townCity || '',
    postalCode: data.postalCode || '',
    nextOfKinName: data.nextOfKinName || '',
    nextOfKinSurname: data.nextOfKinSurname || '',
    nextOfKinContactNumber: data.nextOfKinContactNumber || '',
    nextOfKinAlternateContactNumber: data.nextOfKinAlternateContactNumber || '',
    linkedToCommunity: data.linkedToCommunity,
    linkedToOrganisation: data.linkedToOrganisation,
  };
};

export const formatInstitutionsForFlatFileTemplate = (data: any): string[] => {
  const result = {
    institutionId: data.id || '',
    institution: data.name || '',
    yearEcdStartedOperating: '',
    ecdServiceType: '',
    classification: '',
    isEcdServicePrimarySchoolBased: '',
    numberOfDaysServiceOperate: '',
    hasHolidays: '',
    openingTime: '',
    closingTime: '',
    languageOfLearning: '',
    registrationType: '',
    newOrRenewal: '',
    registrationStatus: '',
    pcrLevel: '',
    certificateNumber: '',
    issueDate: '',
    expiryDate: '',
    progStatus: '',
    progLevel: '',
    progCertificateNumber: '',
    progCertificateIssueDate: '',
    progCertificateExpiryDate: '',
    organisationName: '',
    networkName: '',
    privateOrNpo: '',
    registrationNumber: '',
    registeredAsNpo: '',
    npoNumber: '',
    building: '',
    sharedBuilding: '',
    classrooms: '',
    kitchen: '',
    space: '',
    outdoor: '',
    toilet: '',
    toilets_children: '',
    toilets_gender: '',
    capture_toilets_total: '',
    count_toilets_children: '',
    count_toilets_adults: '',
    heating: '',
    lighting: '',
    cooking: '',
    water: '',
    waterother: '',
    potable: '',
    handwashing: '',
    mealsProvided: '',
    foodProvider: '',
    foodProviderOther: '',
    monthlyFeeRangeLowest: '',
    monthlyFeeRangeHighest: '',
    numberOfStaff: '',
    count_staff_male: '',
    count_staff_female: '',
    count_staff_other: '',
    count_staff_salary_paid: '',
    count_staff_salary_unpaid: '',
    count_staff_contract_permanent: '',
    count_staff_contract_temporary: '',
    count_staff_contract_substitute: '',
    count_staff_time_full: '',
    count_staff_time_part: '',
    count_staff_cat_educator: '',
    count_staff_cat_practitioner: '',
    count_staff_cat_manager: '',
    count_staff_cat_support: '',
    professionals_practitioners: '',
    count_staff_qual_skills: '',
    count_staff_qual_nqf4_5: '',
    count_staff_qual_nqf6_9: '',
    numberOfChildrenEnrolled: '',
    ageRange0To12Months: '',
    ageRange1To2Years: '',
    ageRange2To3Years: '',
    ageRange3To4Years: '',
    ageRange4To5Years: '',
    ageRange5To6Years: '',
    gradeROffered: '',
    mainFundingSource: '',
    fundingother: '',
    receivesDBESubsidy: '',
    confirm_subsidy: '',
    fundedThroughEquitableShare: '',
    numberOfChildrenFunded: '',
    fundedThroughConditionalGrant: '',
    numChildrenSubsidisedCG: '',
    fundedThroughTopUpSubsidy: '',
    numChildrenSubsidisedTU: '',
    assessedForMaintenanceGrant: '',
    receivedMaintenanceGrant: '',
    yearOfReceipt: '',
    infrastructureGrantBudget: '',
    monthlyCGBudgetTotal: '',
    monthlyESBudgetTotal: '',
    registeredCapacity: '',
    ecdEmploymentStimulus: '',
    dateECDESRFReceived: '',
    emisNumber: data.emisNumber || '',
    country: '',
    province: '',
    district: data.district ? data.district.name : '',
    districtMunicipality: '',
    localMunicipality: data.localMunicipality ? data.localMunicipality.localMunicipality : '',
    wardLabel: data.wardLabel || '',
    physicalAddress: '',
    suburb: '',
    townshipVillage: '',
    townCity: '',
    locality: '',
    postalAddress: '',
    postalCode: '',
    gisLatitude: '',
    gisLongitude: '',
    gisAltitude: '',
    gisSource: '',
    nearestPointOfInterest: '',
    pointOfInterest: '',
    officeEmailAddress: '',
    officeContactNumber: '',
    contactPersonDesignation: '',
    contactPersonFullName: '',
    contactPersonContactNumber: '',
    contactPersonEmail: '',
    alternativeContactPersonDesignation: '',
    alternativeContactPersonFullName: '',
    alternativeContactPersonContactNumber: '',
    alternativeContactPersonEmail: '',
    registrationOffice: '',
    identifiedBy: '',
    identifiedDate: '',
    sspSupervisor: '',
    sspSupervisorEmail: '',
    socialServicesProfessional: '',
    socialServicesProfessionalEmail: '',
    duplicateReference: '',
    duplicateAction: '',
    reasonForDuplicateAction: '',
    trackingCode: data.trackingCode || '',
    verified: '',
    validated: '',
    dataSource: '',
    censusDate: '',
    censusId: '',
    isArchived: '',
    monthlyTopUpSubsidyTotal: '',
  };

  return Object.values(result).map((s: any) => `${s}`);
};

export const formatRegistrationsForFlatFileTemplate = (data: any, updateOrNew: string): string[] => {
  const result = {
    ...(updateOrNew === 'update' && {
      id: data.id || '',
      createdAt: data.createdAt || '',
    }),
    institutionId: (data.associatedEcdInstitution && data.associatedEcdInstitution.id) || '',
    institution: (data.associatedEcdInstitution && data.associatedEcdInstitution.name) || data.label || '',
    district:
      (data.associatedEcdInstitution &&
        data.associatedEcdInstitution.district &&
        data.associatedEcdInstitution.district.name) ||
      '',
    localMunicipality:
      (data.associatedEcdInstitution &&
        data.associatedEcdInstitution.localMunicipality &&
        data.associatedEcdInstitution.localMunicipality.localMunicipality) ||
      '',
    wardLabel: (data.associatedEcdInstitution && data.associatedEcdInstitution.wardLabel) || '',
    emisNumber: (data.associatedEcdInstitution && data.associatedEcdInstitution.emisNumber) || '',
    trackingCode: (data.associatedEcdInstitution && data.associatedEcdInstitution.trackingCode) || '',
    identifiedBy: '',
    dateIdentified: '',
    dateAssigned: '',
    socialServicesProfessional: '',
    sspSupervisor: '',
    newOrRenewal: '',
    registrationType: '',
    applicationCollectionType: '',
    dateAttended: '',
    dateInvited: '',
    dateCollected: '',
    applicationSubmissionType: '',
    dateSubmitted: '',
    submissionNumber: '',
    documentReviewStatus: '',
    dateSubmissionCompleted: '',
    assessEcdFrameworkOnline: '',
    applicationNotes: '',
    numberOfChildrenPresent: '',
    numberOfStaffPresent: '',
    registrationOutcome: '',
    siteAssessmentOutcome: '',
    dateOfDSDSiteVisit: '',
    dateOfEHPSiteVisit: '',
    dateEHPReportReceived: '',
    reasonForAssessmentDelay: '',
    otherAssessmentDelayReasons: '',
    dateFileCompilationSubmitted: '',
    programmeAssessmentOutcome: '',
    progRegistrationOutcome: '',
    certificateNumber: '',
    issueDate: '',
    expiryDate: '',
    pcrLevel: '',
    progCertificateNumber: '',
    progCertificateIssueDate: '',
    progCertificateExpiryDate: '',
    progLevel: '',
    dateCertificateReceivedFromDistrict: '',
    reasonForCertificationDelay: '',
    dateCertificateDispensed: '',
    dateProgCertificateReceivedFromDistrict: '',
    reasonForProgCertificationDelay: '',
    dateProgCertificateDispensed: '',
    assessmentNotes: '',
  };

  return Object.values(result).map((s: any) => `${s}`);
};

export const formatPeopleForFlatfileTemplate = (data?: any, updateOrNew?: string): string[] => {
  const result = {
    ...(updateOrNew === 'update' && {
      id: data && data.id,
    }),
    institutionId:
      (data &&
        data.associatedInstitutions &&
        data.associatedInstitutions[0] &&
        data.associatedInstitutions[0].institution &&
        data.associatedInstitutions[0].institution.id) ||
      (data && data.institutions && data.institutions[1] && data.institutions[1].id) ||
      '',
    institution:
      (data &&
        data.associatedInstitutions &&
        data.associatedInstitutions[0] &&
        data.associatedInstitutions[0].institution &&
        data.associatedInstitutions[0].institution.name) ||
      (data && data.institutions && data.institutions[1] && data.institutions[1].name) ||
      '',
    district:
      (data &&
        data.associatedInstitutions &&
        data.associatedInstitutions[0] &&
        data.associatedInstitutions[0].institution &&
        data.associatedInstitutions[0].institution.district &&
        data.associatedInstitutions[0].institution.district.name) ||
      (data &&
        data.institutions &&
        data.institutions[1] &&
        data.institutions[1].district &&
        data.institutions[1].district.name) ||
      '',
    localMunicipality:
      (data &&
        data.associatedInstitutions &&
        data.associatedInstitutions[0] &&
        data.associatedInstitutions[0].institution &&
        data.associatedInstitutions[0].institution.localMunicipality &&
        data.associatedInstitutions[0].institution.localMunicipality.localMunicipality) ||
      (data &&
        data.institutions &&
        data.institutions[1] &&
        data.institutions[1].localMunicipality &&
        data.institutions[1].localMunicipality.localMunicipality) ||
      '',
    wardLabel:
      (data &&
        data.associatedInstitutions &&
        data.associatedInstitutions[0] &&
        data.associatedInstitutions[0].institution &&
        data.associatedInstitutions[0].institution.wardLabel) ||
      (data && data.institutions && data.institutions[1] && data.institutions[1].wardLabel) ||
      '',
    emisNumber:
      (data &&
        data.associatedInstitutions &&
        data.associatedInstitutions[0] &&
        data.associatedInstitutions[0].institution &&
        data.associatedInstitutions[0].institution.emisNumber) ||
      (data && data.institutions && data.institutions[1] && data.institutions[1].emisNumber) ||
      '',
    trackingCode:
      (data &&
        data.associatedInstitutions &&
        data.associatedInstitutions[0] &&
        data.associatedInstitutions[0].institution &&
        data.associatedInstitutions[0].institution.trackingCode) ||
      (data && data.institutions && data.institutions[1] && data.institutions[1].trackingCode) ||
      '',
    type: data && data.type ? data.type.name : '',
    firstName: data.firstName || '',
    surname: data.lastName || '',
    gender: '',
    dateOfBirth: '',
    citizenshipId: '',
    identificationType: '',
    nationalID: '',
    passport: '',
    birthCertificateOrId: '',
    appliedForBirthCertificateOrId: '',
    studyPermit: '',
    studyPermitNumber: '',
    reasonForLackOfBirthCertificateOrId: '',
    populationGroup: '',
    homeLanguage: '',
    contactNumber: '',
    emailAddress: '',
    roadToHealthCard: '',
    reasonForLackOfRoadToHealthCard: '',
    disability: '',
    disabilityTypes: '',
    specialNeeds: '',
    ovc: '',
    receivingSocialGrant: '',
    grantType: '',
    trackingId: '',
    dateStarted: '',
    dateExited: '',
    careGiverType1: '',
    careGiverRelationship1: '',
    careGiverName1: '',
    careGiverSurname1: '',
    careGiverGender1: '',
    caregiverCitizenshipId1: '',
    caregiverIdentificationType1: '',
    caregiverPassport1: '',
    careGiverNationalId1: '',
    careGiverMaritalStatus1: '',
    caregiverCountryCode1: '',
    careGiverContactNumber1: '',
    careGiverEmailAddress1: '',
    careGiverPhysicalAddress1: '',
    employmentStatus1: '',
    careGiverOccupation1: '',
    careGiverEmployer1: '',
    monthlyIncomeRange1: '',
    monthlyIncome1: '',
    incomeSourceDocument1: '',
    childResidesWithThisCaregive1: '',
    accountPayer1: '',
    careGiverType2: '',
    careGiverRelationship2: '',
    careGiverName2: '',
    careGiverSurname2: '',
    careGiverGender2: '',
    caregiverCitizenshipId2: '',
    caregiverIdentificationType2: '',
    caregiverPassport2: '',
    careGiverNationalId2: '',
    careGiverMaritalStatus2: '',
    caregiverCountryCode2: '',
    careGiverContactNumber2: '',
    careGiverEmailAddress2: '',
    careGiverPhysicalAddress2: '',
    employmentStatus2: '',
    careGiverOccupation2: '',
    careGiverEmployer2: '',
    monthlyIncomeRange2: '',
    monthlyIncome2: '',
    incomeSourceDocument2: '',
    childResidesWithThisCaregive2: '',
    accountPayer2: '',
  };

  return Object.values(result).map((s: any) => `${s}`);
};

export const formatManagementForFlatfileTemplate = (data?: any, updateOrNew?: string) => {
  const result = {
    ...(updateOrNew === 'update' && {
      id: data && data.id ? data.id : '',
    }),
    name: data && data.name ? data.name : '',
    district: data && data.district ? data.district.name : '',
    localMunicipality: data && data.localMunicipality ? data.localMunicipality.localMunicipality : '',
    wardLabel: data && data.wardLabel ? data.wardLabel : '',
    emisNumber: data && data.emisNumber ? data.emisNumber : '',
    trackingCode: data && data.trackingCode ? data.trackingCode : '',
    province: '',
    circuit: '',
    registrationOffice: '',
    sspSupervisor: '',
    sspSupervisorEmail: '',
    socialServicesProfessional: '',
    socialServicesProfessionalEmail: '',
  };

  return Object.values(result).map((s: any) => `${s}`);
};

const removeLastCharacterFromObject = (obj: any) => {
  const newObj: any = {};
  let allValuesEmpty = true;

  for (const key in obj) {
    if (obj[key] && obj[key].length > 0) {
      allValuesEmpty = false;
    }

    const newKey = key.slice(0, -1); // Remove the last character from the key
    newObj[newKey] = obj[key];
  }

  if (allValuesEmpty) {
    return undefined;
  }

  return newObj;
};

const formatPhoneNumber = ({ phoneNumber, countryCode }: { phoneNumber: string; countryCode: string }) => {
  countryCode = countryCode.replace('+', '');
  phoneNumber = phoneNumber.replace(/^0+/, '');

  if (phoneNumber.startsWith('+' + countryCode)) {
    return phoneNumber;
  }

  const formattedNumber = `+${countryCode}${phoneNumber}`;

  return formattedNumber;
};

const removeWhiteSpace = (inputString: string) => {
  // Use a regular expression to replace all white spaces with an empty string
  return inputString.replace(/\s/g, '');
};

const monthlyIncomeRangeOptions = [
  {
    label: 'R0 - R3500',
    value: '0To3500',
  },
  {
    label: 'R3501 upwards',
    value: '3501upwards',
  },
];

const computeMonthlyIncomeRangeAndMonthlyIncome = (monthlyIncome: string) => {
  try {
    const monthlyIncomeInt = parseInt(monthlyIncome);
    if (monthlyIncomeInt) {
      if (monthlyIncomeInt <= 3500) {
        return { monthlyIncomeRange: '0To3500', monthlyIncome: monthlyIncomeInt };
      } else if (monthlyIncomeInt > 3500) {
        return { monthlyIncomeRange: '3501upwards', monthlyIncome: monthlyIncomeInt };
      }
    }
  } catch (error) {
    console.log(error);
  }

  return undefined;
};

const formatCaregiverListForBulkUpload = (data: any) => {
  const newData = removeLastCharacterFromObject(data);
  if (newData) {
    const {
      childResidesWithThisCaregive,
      accountPayer,
      caregiverCountryCode,
      careGiverContactNumber,
      monthlyIncomeRange,
      monthlyIncome,
      ...rest
    } = newData;

    let contactNumber = '';
    let monthlyIncomeRangeValue = '';
    let monthlyIncomeValue = undefined;

    if (careGiverContactNumber && caregiverCountryCode) {
      contactNumber = formatPhoneNumber({
        phoneNumber: careGiverContactNumber,
        countryCode: caregiverCountryCode,
      });
    }

    if (monthlyIncome) {
      const result = computeMonthlyIncomeRangeAndMonthlyIncome(monthlyIncome);
      if (result) {
        monthlyIncomeRangeValue = result.monthlyIncomeRange;
        monthlyIncomeValue = result.monthlyIncome;
      }
    }
    if (monthlyIncomeRange) {
      const result = monthlyIncomeRangeOptions.find(
        (a: Option) =>
          removeWhiteSpace(a.label.toLowerCase()) === removeWhiteSpace(monthlyIncomeRange.toString().toLowerCase())
      );
      if (result) {
        monthlyIncomeRangeValue = result.value;
      }
    }

    return [
      {
        ...rest,
        childResidesWithThisCaregive: getBooleanValue(childResidesWithThisCaregive),
        accountPayer: getBooleanValue(accountPayer),
        careGiverContactNumber: contactNumber,
        monthlyIncomeRange: monthlyIncomeRangeValue,
        monthlyIncome: monthlyIncomeValue,
      },
    ];
  }

  return [];
};

export const formatPeopleForBulkUpload = (data: any) => {
  const {
    disability,
    ovc,
    birthCertificateOrId,
    appliedForBirthCertificateOrId,
    receivingSocialGrant,
    careGiverType1,
    careGiverRelationship1,
    careGiverName1,
    careGiverSurname1,
    careGiverGender1,
    caregiverCitizenshipId1,
    caregiverIdentificationType1,
    caregiverPassport1,
    careGiverNationalId1,
    careGiverMaritalStatus1,
    caregiverCountryCode1,
    careGiverContactNumber1,
    careGiverEmailAddress1,
    careGiverPhysicalAddress1,
    employmentStatus1,
    careGiverOccupation1,
    careGiverOccupationOther1,
    careGiverEmployer1,
    monthlyIncomeRange1,
    monthlyIncome1,
    incomeSourceDocument1,
    childResidesWithThisCaregive1,
    accountPayer1,
    careGiverType2,
    careGiverRelationship2,
    careGiverName2,
    careGiverSurname2,
    careGiverGender2,
    caregiverCitizenshipId2,
    caregiverIdentificationType2,
    caregiverPassport2,
    careGiverNationalId2,
    careGiverMaritalStatus2,
    caregiverCountryCode2,
    careGiverContactNumber2,
    careGiverEmailAddress2,
    careGiverPhysicalAddress2,
    employmentStatus2,
    careGiverOccupation2,
    careGiverOccupationOther2,
    careGiverEmployer2,
    monthlyIncomeRange2,
    monthlyIncome2,
    incomeSourceDocument2,
    childResidesWithThisCaregive2,
    accountPayer2,
    dateOfBirth,
    roadtoHealthCard,
    ...rest
  } = data;

  let age, ageRange;

  if (dateOfBirth) {
    age = calculateAge(dateOfBirth);
    const ageRangeOption = determineAgeRange(age);

    if (ageRangeOption) {
      ageRange = ageRangeOption.value;
    }
  }

  const caregiver1 = formatCaregiverListForBulkUpload({
    careGiverType1,
    careGiverRelationship1,
    careGiverName1,
    careGiverSurname1,
    careGiverGender1,
    caregiverCitizenshipId1,
    caregiverIdentificationType1,
    caregiverPassport1,
    careGiverNationalId1,
    careGiverMaritalStatus1,
    caregiverCountryCode1,
    careGiverContactNumber1,
    careGiverEmailAddress1,
    careGiverPhysicalAddress1,
    employmentStatus1,
    careGiverOccupation1,
    careGiverOccupationOther1,
    careGiverEmployer1,
    monthlyIncomeRange1,
    monthlyIncome1,
    incomeSourceDocument1,
    childResidesWithThisCaregive1,
    accountPayer1,
  });

  const caregiver2 = formatCaregiverListForBulkUpload({
    careGiverType2,
    careGiverRelationship2,
    careGiverName2,
    careGiverSurname2,
    careGiverGender2,
    caregiverCitizenshipId2,
    caregiverIdentificationType2,
    caregiverPassport2,
    careGiverNationalId2,
    careGiverMaritalStatus2,
    caregiverCountryCode2,
    careGiverContactNumber2,
    careGiverEmailAddress2,
    careGiverPhysicalAddress2,
    employmentStatus2,
    careGiverOccupation2,
    careGiverOccupationOther2,
    careGiverEmployer2,
    monthlyIncomeRange2,
    monthlyIncome2,
    incomeSourceDocument2,
    childResidesWithThisCaregive2,
    accountPayer2,
  });

  const caregiverList = [...caregiver1, ...caregiver2];

  return {
    ...rest,
    age,
    ageRange,
    ovc: getBooleanValue(ovc),
    additionalInstitution: rest.institution,
    disability: getBooleanValue(disability),
    birthCertificateOrId: getBooleanValue(birthCertificateOrId),
    appliedForBirthCertificateOrId: getBooleanValue(appliedForBirthCertificateOrId),
    receivingSocialGrant: getBooleanValue(receivingSocialGrant),
    immunizationCard: getBooleanValue(roadtoHealthCard),
    dateOfBirth: new Date(dateOfBirth),
    caregiverList,
  };
};

export const formatRegistrationsForBulkUpload = (data: any) => {
  const {
    assessEcdFrameworkOnline,
    submitted,
    approved,
    numberOfStaffPresent,
    numberOfChildrenPresent,
    dateAssigned,
    dateAttended,
    dateCertificateReceivedFromDistrict,
    dateCollected,
    dateEHPReportReceived,
    dateCertificateDispensed,
    dateFileCompilationSubmitted,
    dateIdentified,
    dateOfDSDSiteVisit,
    dateOfEHPSiteVisit,
    dateProgCertificateReceivedFromDistrict,
    dateProgCertificateDispensed,
    dateSubmissionCompleted,
    dateSubmitted,
    dateInvited,
    expiryDate,
    issueDate,
    ...rest
  } = data;

  const datesFields = {
    dateAssigned,
    dateAttended,
    dateCertificateReceivedFromDistrict,
    dateCollected,
    dateEHPReportReceived,
    dateCertificateDispensed,
    dateFileCompilationSubmitted,
    dateIdentified,
    dateOfDSDSiteVisit,
    dateOfEHPSiteVisit,
    dateProgCertificateReceivedFromDistrict,
    dateProgCertificateDispensed,
    dateSubmissionCompleted,
    dateSubmitted,
    dateInvited,
    expiryDate,
    issueDate,
  };

  let dates: any = {};
  let nonDatesFields: any = {};

  Object.entries(rest).forEach(([key, value]: any) => {
    if (typeof value === 'string' && value) {
      nonDatesFields[key] = value;
    } else {
      nonDatesFields[key] = undefined;
    }
  });

  Object.entries(datesFields).forEach(([key, value]: any) => {
    if (value && isValidDate(value)) {
      dates[key] = new Date(value);
    } else {
      dates[key] = undefined;
    }
  });

  return {
    ...nonDatesFields,
    ...dates,
    assessEcdFrameworkOnline: getBooleanValue(assessEcdFrameworkOnline),
    submitted: getBooleanValue(submitted),
    approved: getBooleanValue(approved),
    numberOfStaffPresent: parseInt(numberOfStaffPresent) ? parseInt(numberOfStaffPresent) : undefined,
    numberOfChildrenPresent: parseInt(numberOfChildrenPresent) ? parseInt(numberOfChildrenPresent) : undefined,
  };
};

export const formatManagementForBulkUpload = (institution: any) => {
  return {
    id: institution.id,
    emisNumber: institution.emisNumber,
    trackingCode: institution.trackingCode,
    name: institution.institution || '',
    wardLabel: institution.wardLabel,
    district: institution.district,
    localMunicipality: institution.localMunicipality,
    province: institution.province,
    registrationOffice: institution.registrationOffice,
    socialServicesProfessional: institution.socialServicesProfessional,
    socialServicesProfessionalEmail: institution.socialServicesProfessionalEmail,
    sspSupervisor: institution.sspSupervisor,
    sspSupervisorEmail: institution.sspSupervisorEmail,
  };
};

export const formatCaregiverList = (data: any): CaregiverType => {
  return {
    id: data.id,
    careGiverContactNumber: data.careGiverContactNumber || '',
    careGiverEmailAddress: data.careGiverEmailAddress || '',
    careGiverEmployer: data.careGiverEmployer || '',
    careGiverOccupationOther: data.careGiverOccupationOther || '',
    careGiverMaritalStatus: formatValues(data.careGiverMaritalStatus),
    careGiverName: data.careGiverName || '',
    careGiverNationalId: data.careGiverNationalId || '',
    careGiverOccupation: formatValues(data.careGiverOccupation),
    careGiverPhysicalAddress: data.careGiverPhysicalAddress || '',
    careGiverRelationship: formatValues(data.careGiverRelationship),
    careGiverSurname: data.careGiverSurname || '',
    careGiverType: formatValues(data.careGiverType),
    caregiverPassport: data.caregiverPassport || '',
    employmentStatus: formatValues(data.employmentStatus),
    reasonExited: data.reasonExited || '',
    monthlyIncomeRange: formatValues(data.monthlyIncomeRange),
    monthlyIncome: data.monthlyIncome,
    incomeSourceDocument: formatValues(data.incomeSourceDocument),
    caregiverIdentificationType: formatValues(data.caregiverIdentificationType),
    proofOfIncomeFile: data.proofOfIncomeFile ? data.proofOfIncomeFile.id : undefined,
    roadToHealthCardFile: data.roadToHealthCardFile ? data.roadToHealthCardFile.id : undefined,
    careGiverGender: formatValues(data.careGiverGender),
    caregiverCitizenshipId: formatValues(data.caregiverCitizenship),
    childResidesWithThisCaregive: formatValues(data.childResidesWithThisCaregive),
    accountPayer: formatValues(data.accountPayer),
  };
};

export const formatPeopleProfileInstitutionFormType = (data: any): PeopleProfileInstitutionFormType => {
  return {
    id: data.id,
    designation: data.designation || '',
    endDate: data.endDate ? new Date(data.endDate) : new Date('31 December 9999'),
    startDate: data.startDate ? new Date(data.startDate) : new Date('1 January 1970'),
    positionOther: data.positionOther || '',
    postLevel: formatNumberValues(data.postLevel),
    primary: formatBoolean(data.primary, false),
    gradeIds: data.grades ? data.grades.map((g: any) => ({ value: g.id, label: g.value })) : [],
    institution: data.institution
      ? {
          id: data.institution.id,
          name: data.institution.name,
          emisNumber: data.institution.emisNumber || '',
          district: data.institution.district
            ? {
                id: data.institution.district.id,
                name: data.institution.district.name,
              }
            : null,
          type: data.institution.type
            ? {
                id: data.institution.type.id,
                name: data.institution.type.name,
              }
            : null,
          province: data.institution.province
            ? {
                id: data.institution.province.id,
                name: data.institution.province.name,
              }
            : null,
          localMunicipality: data.institution.localMunicipality
            ? {
                id: data.institution.localMunicipality.id,
                name: data.institution.localMunicipality.localMunicipality,
              }
            : null,
          communityType: data.institution.communityType
            ? {
                id: data.institution.communityType.id,
                name: data.institution.communityType.name,
              }
            : null,
          districtMunicipality: data.institution.districtMunicipality
            ? {
                id: data.institution.districtMunicipality.id,
                name: data.institution.districtMunicipality.name,
              }
            : null,
        }
      : undefined,
    phaseIds: formatArrayValues(data.phases) || [],
    positionId: formatValues(data.position),
    progressionStatusId: formatValues(data.progressionStatus),
    subjectIds: formatArrayValues(data.subjects) || [],
    typeId: formatValues(data.type),
    year: formatValues(data.year),
    registrationDate: data.registrationDate ? new Date(data.registrationDate) : null,
    dateExited: data.dateExited ? new Date(data.dateExited) : null,
    trackingId: data.trackingId || '',
    ageCohort: formatValues(data.ageCohort),
    reasonForTerminationId: formatValues(data.reasonForTermination),
    linkedToAnInstitution: data.linkedToAnInstitution,
    linkedToAnOrganisation: data.linkedToAnOrganisation,
    roleId: formatValues(data.role),
  };
};

export const formatPeopleProfileOrganisationFormType = (data: any): PeopleProfileOrganisationFormType => {
  return {
    id: data.id,
    designation: data.designation || '',
    primary: formatBoolean(data.primary, false),
    organisation: data.organisation
      ? { id: data.organisation.id, name: getOrganisationName(data.organisation) }
      : undefined,
  };
};

export const getPeopleProfileInstitutionFormTypeDefault = (): PeopleProfileInstitutionFormType => {
  return {
    id: '',
    designation: '',
    startDate: new Date('1 January 1970'),
    endDate: new Date('31 December 9999'),
    postLevel: undefined,
    primary: false,
    typeId: undefined,
    positionId: undefined,
    positionOther: '',
    phaseIds: [],
    gradeIds: [],
    subjectIds: [],
    institution: undefined,
    progressionStatusId: undefined,
    year: undefined,
    registrationDate: null,
    dateExited: null,
    trackingId: '',
    ageCohort: undefined,
    reasonForTerminationId: undefined,
    linkedToAnInstitution: 'NO',
    linkedToAnOrganisation: 'NO',
    roleId: undefined,
  };
};

export const getPeopleProfileOrganisationFormTypeDefault = (): PeopleProfileOrganisationFormType => {
  return {
    id: '',
    designation: '',
    primary: false,
    organisation: undefined,
  };
};

export const getInsightsOrganisationFormTypeDefault = (): InsightsOrganisationFormType => {
  return {
    id: '',
    dateCreated: new Date(),
    dateLastModified: new Date(),
    dateArchived: undefined,
    status: undefined,
    organisation: undefined,
    users: [],
  };
};

export const formatLayoutsInsightsData = (data: any[]): LayoutsInsightsType[] => {
  return data.map(data => {
    return {
      id: data.id,
      type: data.__typename,
      name: data.dashboardName ? data.dashboardName : data.insightName,
      category: data.category,
      assignedTo: (data.assignedTo && formatArrayValues(data.assignedTo)) || [],
      users: (data.users && formatArrayValues(data.users)) || [],
      isDefault: data.isDefault,
      createdBy: (data.createdBy && formatUserValues(data.createdBy)) || undefined,
      createdOn: data.createdOn,
      tags: (data.tags && formatArrayValues(data.tags)) || [],
      currentOrganisationUsers: data.currentOrganisationUsers,
    };
  });
};

export const LayoutsInsightsTypeDefault: LayoutsInsightsType = {
  id: '',
  name: '',
  type: '',
  category: '',
  isDefault: false,
  assignedTo: [],
  users: [],
  createdBy: undefined,
  createdOn: null,
  tags: [],
};

export const getUserOrganisationFormTypeDefault = (): UserOrganisationFormType => {
  return {
    id: '',
    dateCreated: new Date(),
    dateLastModified: new Date(),
    dateArchived: undefined,
    systemRole: undefined,
    practitionerType: undefined,
    status: undefined,
    isDefault: false,
    organisation: undefined,
    user: undefined,
    registrationRole: undefined,
    dataRole: undefined,
  };
};

export const getOrganisationTypeOption = (org: {
  id: string;
  funder?: {
    name: string;
  } | null;
  serviceProvider?: {
    name: string;
  } | null;
  institution?: {
    name: string;
  } | null;
}): Option => {
  return {
    value: org.funder ? 'funder' : org.serviceProvider ? 'serviceProvider' : 'institution',
    label: getOrganisationName(org),
  };
};

const typesOfToiletsOptions = [
  {
    label: 'Flush toilet (connected to sewage systems)',
    value: '1',
  },
  {
    label: 'Flush toilet (with septic tank)',
    value: '2',
  },
  {
    label: 'Chemical toilet',
    value: '3',
  },
  {
    label: 'Pit latrine with ventilation (VIP)',
    value: '4',
  },
  {
    label: 'Pit latrine without ventilation',
    value: '5',
  },
  { label: 'Bucket latrine', value: '6' },
  { label: 'Potties', value: '7' },
  { label: 'Toilets for people with disabilities', value: '8' },
  { label: 'None', value: '0' },
  { label: 'Other', value: '97' },
];

const outdoorEquipmentOptions = [
  { label: 'None', value: '0' },
  { label: 'Jungle gym', value: '1' },
  { label: 'Swings', value: '2' },
  { label: 'Slide', value: '3' },
  { label: 'Sand tray or pit', value: '4' },
];

const foodProviderOptions = [
  { label: 'ECD Programme', value: 1 },
  { label: 'Parents', value: 2 },
  { label: 'Government', value: 3 },
  { label: 'NGO or NPO', value: 4 },
  { label: 'Other', value: 97 },
];

const mealsProvidedOptions = [
  { label: 'Breakfast', value: 1 },
  { label: 'Lunch', value: 2 },
  {
    label: 'Dinner or Afternoon meal',
    value: 3,
  },
  { label: 'Snacks between meals', value: 4 },
  { label: 'Bottle feeding', value: 5 },
  { label: 'None', value: 0 },
];

const languageOfLearningOptions = [
  { label: 'Afrikaans', value: '1' },
  { label: 'English', value: '2' },
  { label: 'IsiNdebele', value: '3' },
  { label: 'IsiXhosa', value: '4' },
  { label: 'IsiZulu', value: '5' },
  { label: 'Sepedi', value: '6' },
  { label: 'Setswana', value: '7' },
  { label: 'Sesotho', value: '8' },
  { label: 'Siswati', value: '9' },
  { label: 'Tshivenda', value: '10' },
  { label: 'Xitsonga', value: '11' },
  { label: 'Sign Language', value: '12' },
  { label: 'Other', value: '97' },
];

const nearestPointOfInterestOptions = [
  { label: 'Police Station', value: 'Police Station' },
  { label: 'School', value: 'School' },
  { label: 'Clinic', value: 'Clinic' },
  { label: 'Post Office', value: 'Post Office' },
  { label: 'Church', value: 'Church' },
  { label: 'Tuckshop', value: 'Tuckshop' },
  { label: 'Sangoma', value: 'Sangoma' },
  { label: 'Other', value: 'Other' },
];

const sharedBuildingOptions = [
  { label: 'Only this ECD programme', value: 'Only this ECD programme' },
  {
    label: 'Other activities as well',
    value: 'Other activities as well',
  },
];

const buildingOptions = [
  {
    label: 'Conventional brick or block with tile or zinc roof',
    value: 'Conventional brick or block with tile or zinc roof',
  },
  {
    label: 'Traditional, mortar or mud walls with zinc or thatch roof',
    value: 'Traditional, mortar or mud walls with zinc or thatch roof',
  },
  {
    label: 'Shipping container',
    value: 'Shipping container',
  },
  {
    label: 'Prefab building',
    value: 'Prefab building',
  },
  {
    label: 'Informal housing (shack)',
    value: 'Informal housing (shack)',
  },
  {
    label: 'Other',
    value: 'Other',
  },
];

const separateKitchenOptions = [
  { label: 'Yes', value: 'Yes' },
  {
    label: 'No',
    value: 'No',
  },
  {
    label: 'There is no kitchen or meal preparation area',
    value: 'There is no kitchen or meal preparation area',
  },
];

const getBooleanValue = (label?: string | boolean) => {
  if (label !== undefined) {
    if (typeof label === 'boolean') {
      return label;
    }

    if (label.toLowerCase() === 'true' || label.toLowerCase() === 'yes') {
      return true;
    } else if (label.toLowerCase() === 'false' || label.toLowerCase() === 'no') {
      return false;
    }
  }

  return undefined;
};

const getRegistredAsNpo = (label?: string | boolean) => {
  if (label !== undefined) {
    if (typeof label === 'boolean') {
      if (label) {
        return 1;
      } else {
        return 0;
      }
    }

    if (label.toLowerCase() === 'true' || label.toLowerCase() === 'yes') {
      return 1;
    } else if (label.toLowerCase() === 'false' || label.toLowerCase() === 'no') {
      return 0;
    }
  }

  return undefined;
};

const getArrayString = (options: any, labels?: any) => {
  if (labels) {
    const labelsArray = labels.split(',');
    const result = labelsArray
      .map((a: any) => {
        const option = options.find((b: any) => b.label.toLowerCase() === a.toLowerCase());
        if (option) {
          return option.value;
        } else {
          return null;
        }
      })
      .filter((c: any) => c !== null);

    return result.toString();
  }

  return undefined;
};

const getStringValue = (options: any, label?: any) => {
  if (label) {
    const option = options.find((b: any) => b.label.toLowerCase() === label.toLowerCase());
    if (option) {
      return option.value;
    } else {
      return undefined;
    }
  }

  return undefined;
};

const getNewOrRenewalValue = (label: any) => {
  if (label && label.toLowerCase() === 'new') {
    return 'New';
  } else if (label && label.toLowerCase() === 'renewal') {
    return 'Renewal';
  }

  return undefined;
};

const getClassificationValue = (label: any) => {
  if (label && ['centre based', 'center based'].includes(label.toLowerCase())) {
    return 'Centre based';
  } else if (
    label &&
    ['non-centre based', 'non center based', 'non centre based', 'non-center based'].includes(label.toLowerCase())
  ) {
    return 'Non-centre based';
  }

  return undefined;
};

export const formatFormInstitutionToDBInstitutionForBulkUpload = (institituionObject: any, tenDayView: boolean) => {
  let institution: any = {};
  Object.entries(institituionObject).forEach(([key, value]: any) => {
    if (value) {
      if (
        [
          'monthlyFeeRangeHighest',
          'monthlyFeeRangeLowest',
          'numberOfChildrenFunded',
          'numChildrenSubsidisedCG',
          'numChildrenSubsidisedTU',
          'monthlyESBudgetTotal',
          'monthlyCGBudgetTotal',
          'monthlyTopUpSubsidyTotal',
          'yearEcdStartedOperating',
          'ageRange3To4Years',
          'ageRange3To4Years',
          'ageRange4To5Years',
          'ageRange5To6Years',
          'ageRange0To12Months',
          'ageRange1To2Years',
          'ageRange2To3Years',
          'numberOfChildrenEnrolled',
          'numberOfStaff',
          'count_staff_qual_nqf6_9',
          'count_staff_qual_nqf4_5',
          'count_staff_time_part',
          'count_staff_time_full',
          'count_staff_cat_support',
          'count_staff_cat_manager',
          'count_staff_cat_practitioner',
          'count_staff_cat_educator',
          'count_staff_other',
          'count_staff_male',
          'count_staff_female',
          'count_staff_salary_unpaid',
          'count_staff_salary_paid',
          'numToilets',
          'childrenToilets',
          'adultToilets',
          'numberOfClassrooms',
          'infrastructureGrantBudget',
          'registeredCapacity',
        ].includes(key)
      ) {
        institution[`${key}`] = !isNaN(value) ? Number(value) : undefined;
      } else if (key.toLowerCase().includes('date') && key.toLowerCase() !== 'validated') {
        institution[`${key}`] = value && isValidDate(value) ? new Date(value) : undefined;
      } else {
        institution[`${key}`] = value ? value.toString() : undefined;
      }
    }
  });

  return {
    addressee: institution.addressee,
    allocation: institution.allocation,
    certificateNumber: institution.certificateNumber,
    classification: getClassificationValue(institution.classification),
    componentNumber: institution.componentNumber,
    dateEcdEsrfReceived: institution.dateEcdEsrfReceived,
    duplicateAction: institution.duplicateAction,
    duplicateReference: institution.duplicateReference,
    ecdRegistrationProgress: institution.ecdRegistrationProgress,
    ecdServiceType: institution.ecdServiceType,
    educatorNumber: institution.educatorNumber,
    emisNumber: institution.emisNumber,
    trackingCode: institution.trackingCode,
    exDept: institution.exDept,
    examCentre: institution.examCentre,
    examNumber: institution.examNumber,
    expiryDate: institution.expiryDate,
    facsimile: institution.facsimile,
    gisAltitude: institution.gisAltitude,
    gisLatitude: institution.gisLatitude,
    gisLongitude: institution.gisLongitude,
    gisSource: institution.gisSource,
    id: institution.id || institution.institutionId,
    censusID: institution.censusID,
    censusDate: institution.censusDate,
    identifiedBy: institution.identifiedBy,
    identifiedDate: institution.identifiedDate,
    isArchived: institution.isArchived,
    issueDate: institution.issueDate,
    numberOfStaff: institution.numberOfStaff,
    ageRange3To4Years: institution.ageRange3To4Years,
    ageRange4To5Years: institution.ageRange4To5Years,
    ageRange5To6Years: institution.ageRange5To6Years,
    learnerNumber: institution.learnerNumber,
    locality: institution.locality,
    yearEcdStartedOperating: institution.yearEcdStartedOperating,
    name: institution.institution || '',
    nas: institution.nas,
    nearestPointOfInterest: getStringValue(nearestPointOfInterestOptions, institution.nearestPointOfInterest),
    newEmisNumber: institution.newEmisNumber,
    newOrRenewal: getNewOrRenewalValue(institution.newOrRenewal),
    noFeeSchool: institution.noFeeSchool,
    nodalArea: institution.nodalArea,
    npoNumber: institution.npoNumber,
    numberOfChildrenFunded: institution.numberOfChildrenFunded,
    officeContactNumber: institution.officeContactNumber,
    officeEmailAddress: institution.officeEmailAddress,
    organisationName: institution.organisationName,
    networkName: institution.networkName,
    openBoardingSchool: institution.openBoardingSchool,
    ownerBuild: institution.ownerBuild,
    ownerLand: institution.ownerLand,
    ownership: institution.ownership,
    paypointNumber: institution.paypointNumber,
    pcrLevel: institution.pcrLevel,
    physicalAddress: institution.physicalAddress,
    pointOfInterest: institution.pointOfInterest,
    postalAddress: institution.postalAddress,
    postalCode: institution.postalCode,
    privateOrNpo: institution.privateOrNpo,
    progCertificateExpiryDate: institution.progCertificateExpiryDate,
    progCertificateIssueDate: institution.progCertificateIssueDate,
    progCertificateNumber: institution.progCertificateNumber,
    progLevel: institution.progLevel,
    provinceCD: institution.provinceCD,
    quintile: institution.quintile,
    reasonForDuplicateAction: institution.reasonForDuplicateAction,
    region: institution.region,
    registrationDate: institution.registrationDate,
    registrationNumber: institution.registrationNumber,
    section21: institution.section21,
    section21Function: institution.section21Function,
    specialization: institution.specialization,
    status: institution.status,
    streetAddress: institution.streetAddress,
    streetNo: institution.streetNo,
    suburb: institution.suburb,
    townCity: institution.townCity,
    townshipVillage: institution.townshipVillage,
    streetBuilding: institution.streetBuilding,
    area: institution.area,
    verified: institution.verified,
    validated: institution.validated,
    wardLabel: institution.wardLabel,
    year: institution.year,
    yearOfReceipt: institution.yearOfReceipt,
    languageOfLearning: getArrayString(languageOfLearningOptions, institution.languageOfLearning),
    closingTime: institution.closingTime,
    openingTime: institution.openingTime,
    isEcdServicePrimarySchoolBased: getBooleanValue(institution.isEcdServicePrimarySchoolBased),
    hasHolidays: getBooleanValue(institution.hasHolidays),
    numberOfDaysServiceOperate: institution.numberOfDaysServiceOperate,
    registeredAsNpo: getRegistredAsNpo(institution.registeredAsNpo),
    alternativeContactPersonContactNumber: institution.alternativeContactPersonContactNumber,
    alternativeContactPersonDesignation: institution.alternativeContactPersonDesignation,
    alternativeContactPersonEmail: institution.alternativeContactPersonEmail,
    alternativeContactPersonFullName: institution.alternativeContactPersonFullName,
    contactPersonContactNumber: institution.contactPersonContactNumber,
    contactPersonDesignation: institution.contactPersonDesignation,
    contactPersonEmail: institution.contactPersonEmail,
    contactPersonFullName: institution.contactPersonFullName,
    country: institution.country,
    numberOfChildrenEnrolled: institution.numberOfChildrenEnrolled,
    district: institution.district,
    districtMunicipality: institution.districtMunicipality,
    fundingClassification: institution.fundingClassification,
    localMunicipality: institution.localMunicipality,
    progStatus: institution.progStatus,
    province: institution.province,
    //to get value
    registrationOffice: institution.registrationOffice,
    registrationStatus: institution.registrationStatus,
    registrationType: institution.registrationType,
    socialServicesProfessional: institution.socialServicesProfessional,
    socialServicesProfessionalEmail: institution.socialServicesProfessionalEmail,
    sspSupervisor: institution.sspSupervisor,
    sspSupervisorEmail: institution.sspSupervisorEmail,
    foodProvider: getArrayString(foodProviderOptions, institution.foodProvider),
    foodProviderOther: institution.foodProviderOther,
    mealsProvided: getArrayString(mealsProvidedOptions, institution.mealsProvided),
    fundedThroughConditionalGrant: getBooleanValue(institution.fundedThroughConditionalGrant),
    fundedThroughEquitableShare: getBooleanValue(institution.fundedThroughEquitableShare),
    fundedThroughTopUpSubsidy: getBooleanValue(institution.fundedThroughTopUpSubsidy),
    monthlyFeeRangeHighest: institution.monthlyFeeRangeHighest,
    monthlyFeeRangeLowest: institution.monthlyFeeRangeLowest,
    assessedForMaintenanceGrant: getBooleanValue(institution.assessedForMaintenanceGrant),
    receivedMaintenanceGrant: getBooleanValue(institution.receivedMaintenanceGrant),
    feesCharged: getBooleanValue(institution.feesCharged),
    numChildrenSubsidisedCG: institution.numChildrenSubsidisedCG,
    numChildrenSubsidisedTU: institution.numChildrenSubsidisedTU,
    mainFundingSource: institution.mainFundingSource,
    bankAccount: getBooleanValue(institution.bankAccount),
    receivesDBESubsidy: getBooleanValue(institution.receivesDBESubsidy),
    monthlyESBudgetTotal: institution.monthlyESBudgetTotal,
    monthlyCGBudgetTotal: institution.monthlyCGBudgetTotal,
    monthlyTopUpSubsidyTotal: institution.monthlyTopUpSubsidyTotal,
    infrastructureGrantBudget: institution.infrastructureGrantBudget,

    ecdEnrolmentDataCurrent: {
      ...(!tenDayView && {
        gradeROffered: getBooleanValue(institution.gradeROffered),
        ageRange3To4Years: institution.ageRange3To4Years,
        ageRange4To5Years: institution.ageRange4To5Years,
        ageRange5To6Years: institution.ageRange5To6Years,
        ageRange0To12Months: institution.ageRange0To12Months,
        ageRange1To2Years: institution.ageRange1To2Years,
        ageRange2To3Years: institution.ageRange2To3Years,
        numberOfChildrenEnrolled: institution.numberOfChildrenEnrolled,
        registeredCapacity: institution.registeredCapacity,
      }),
    },
    ecdEnrolmentData10Day: {
      ...(tenDayView && {
        gradeROffered: getBooleanValue(institution.gradeROffered),
        ageRange3To4Years: institution.ageRange3To4Years,
        ageRange4To5Years: institution.ageRange4To5Years,
        ageRange5To6Years: institution.ageRange5To6Years,
        ageRange0To12Months: institution.ageRange0To12Months,
        ageRange1To2Years: institution.ageRange1To2Years,
        ageRange2To3Years: institution.ageRange2To3Years,
        numberOfChildrenEnrolled: institution.numberOfChildrenEnrolled,
        registeredCapacity: institution.registeredCapacity,
      }),
    },
    ecdFacilityDetail: {
      landowner: institution.landowner,
      landownerOther: institution.landownerOther,
      buildingOwner: institution.buildingOwner,
      buildingOwnerOther: institution.buildingOwnerOther,
      building: getStringValue(buildingOptions, institution.building),
      buildingOther: institution.buildingOther,
      sharedBuilding: getStringValue(sharedBuildingOptions, institution.sharedBuilding),
      numberOfClassrooms: institution.numberOfClassrooms,
      kitchen: getStringValue(separateKitchenOptions, institution.kitchen),
      separateRoomAsOffice: getBooleanValue(institution.separateRoomAsOffice),
      outdoorPlayArea: getBooleanValue(institution.outdoorPlayArea),
      fence: getBooleanValue(institution.fence),
      gate: getBooleanValue(institution.gate),
      outdoorEquipment: getArrayString(outdoorEquipmentOptions, institution.outdoorEquipment),
      whatHandwashingFacility: institution.whatHandwashingFacility,
      mainSourceOfPowerLighting: institution.mainSourceOfPowerLighting,
      mainSourceOfPowerCooking: institution.mainSourceOfPowerCooking,
      mainSourceOfPowerHeating: institution.mainSourceOfPowerHeating,
      mainSourceOfWater: institution.mainSourceOfWater,
      mainSourceOfWaterOther: institution.mainSourceOfWaterOther,
      separateToilets: getBooleanValue(institution.separateToilets),
      childrenToilets: institution.childrenToilets,
      adultToilets: institution.adultToilets,
      numToilets: institution.numToilets,
      typesOfToilets: getArrayString(typesOfToiletsOptions, institution.typesOfToilets),
    },
    ecdStaffDataCurrent: {
      ...(!tenDayView && {
        paid: institution.count_staff_salary_paid,
        unpad: institution.count_staff_salary_unpaid,
        female: institution.count_staff_female,
        male: institution.count_staff_male,
        genderOther: institution.count_staff_other,
        gradeREducators: institution.count_staff_cat_educator,
        ecdPractitioners: institution.count_staff_cat_practitioner,
        managerialStaff: institution.count_staff_cat_manager,
        supportStuff: institution.count_staff_cat_support,
        fullTime: institution.count_staff_time_full,
        partTime: institution.count_staff_time_part,
        nqfLevel4to5ECD: institution.count_staff_qual_nqf4_5,
        nqfLevel6to9ECD: institution.count_staff_qual_nqf6_9,
        numberOfStaff: institution.numberOfStaff,
      }),
    },
    ecdStaffData10Day: {
      ...(tenDayView && {
        paid: institution.count_staff_salary_paid,
        unpad: institution.count_staff_salary_unpaid,
        female: institution.count_staff_female,
        male: institution.count_staff_male,
        genderOther: institution.count_staff_other,
        gradeREducators: institution.count_staff_cat_educator,
        ecdPractitioners: institution.count_staff_cat_practitioner,
        managerialStaff: institution.count_staff_cat_manager,
        supportStuff: institution.count_staff_cat_support,
        fullTime: institution.count_staff_time_full,
        partTime: institution.count_staff_time_part,
        nqfLevel4to5ECD: institution.count_staff_qual_nqf4_5,
        nqfLevel6to9ECD: institution.count_staff_qual_nqf6_9,
        numberOfStaff: institution.numberOfStaff,
      }),
    },
  };
};

function escapeCSVField(field: any) {
  if (
    typeof field === 'string' &&
    (field.includes(',') || field.includes('\n') || field.includes('\r') || field.includes('"'))
  ) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

export const generateSubmissionsCSV = (data: any) => {
  const allKeys = data.reduce((keys: any, row: any) => {
    Object.keys(row).forEach((key: any) => {
      if (!keys.includes(key)) {
        keys.push(key);
      }
    });
    return keys;
  }, []);

  let csvRows = data.map((row: any) => {
    const rowData = allKeys.map((key: any) => {
      return row[key] || '';
    });

    return rowData;
  });

  let csvContent;

  if (csvRows.length > 0) {
    csvContent = [allKeys, ...csvRows].map(row => row.map(escapeCSVField).join(',')).join('\n');
  } else {
    csvContent = 'submissionDate,startTime,endTime,userId,username,status,form_version,submissionId';
  }

  return csvContent;
};

export const generateSubmissionsHeaders = (data: any) => {
  const allKeys = data.reduce((keys: any, row: any) => {
    Object.keys(row).forEach((key: any) => {
      if (!keys.includes(key)) {
        keys.push(key);
      }
    });
    return keys;
  }, []);

  let csvRows = data.map((row: any) => {
    const rowData = allKeys.map((key: any) => {
      return row[key] || '';
    });

    return rowData;
  });

  let csvContent;

  if (csvRows.length > 0) {
    // csvContent = [allKeys, ...csvRows].map(row => row.map(escapeCSVField).join(',')).join('\n')
    csvContent = allKeys;
  } else {
    csvContent = [
      'submissionDate',
      'startTime',
      'endTime',
      'userId',
      'username',
      'status',
      'form_version',
      'submissionId',
    ];
  }

  return csvContent;
};

export const formatWorkspaceFormSubmissionsExportData = (form: any, submissions: any) => {
  const date = form && form.template && form.template.updatedAt ? form.template.updatedAt : new Date();
  const formVersion = form && form.template && `${format(new Date(date), 'dd/MM/yyyy HH:mm')}`;

  if (submissions && submissions.length) {
    const formatted = submissions.map(({ node }: any) => {
      let questionsAndAnswers: any;

      if (node.data) {
        node.data.forEach((b: any) => {
          questionsAndAnswers = {
            ...questionsAndAnswers,
            [`${b.question.name}`]: b.answer,
          };
        });

        return {
          submissionDate: node.createdAt ? format(new Date(node.createdAt), 'dd/MM/yyyy HH:mm') : '',
          startTime: '',
          endTime: '',
          userId: !!node.respondent ? node.respondent.id : '',
          username: !!node.respondent ? `${node.respondent.firstName}${node.respondent.lastName}` : '',
          ...questionsAndAnswers,
          status: node.status,
          form_version: formVersion,
          submissionId: node.id,
        };
      } else {
        return {
          submissionDate: '',
          startTime: '',
          endTime: '',
          userId: !!node.respondent ? node.respondent.id : '',
          username: !!node.respondent ? `${node.respondent.firstName}${node.respondent.lastName}` : '',
          status: node.status || '',
          form_version: formVersion || '',
          submissionId: node.id || '',
        };
      }
    });
    return formatted;
  } else {
    return [];
  }
};

export const formatSubmissionsExportData = (version: any, submissions: any) => {
  let formVersion: any;

  try {
    const date = version ? new Date(version) : new Date();
    formVersion = `${format(date, 'dd/MM/yyyy HH:mm')}`;
  } catch {
    formVersion = `${format(new Date(), 'dd/MM/yyyy HH:mm')}`;
  }

  if (submissions && submissions.length) {
    const formatted = submissions.map(({ node }: any) => {
      let questionsAndAnswers: any;

      if (node.data) {
        node.data.forEach((b: any) => {
          let answer;

          try {
            answer = JSON.parse(b.answer);
            answer = JSON.stringify(answer).replace(/"/g, '""');
          } catch {
            answer = b.answer;
          }

          questionsAndAnswers = {
            ...questionsAndAnswers,
            [`${b.question.name}`]: answer,
          };
        });

        return {
          submissionDate: node.createdAt ? format(new Date(node.createdAt), 'dd/MM/yyyy HH:mm') : '',
          startTime: '',
          endTime: '',
          userId: node.respondent && node.respondent.id,
          username: node.respondent && `${node.respondent.firstName}` + `${node.respondent.lastName}`,
          ...questionsAndAnswers,
          status: node.status,
          form_version: formVersion,
          submissionId: node.id,
        };
      } else {
        return {
          submissionDate: '',
          startTime: '',
          endTime: '',
          userId: (node.respondent && node.respondent.id) || '',
          username: (node.respondent && `${node.respondent.firstName}` + `${node.respondent.lastName}`) || '',
          status: node.status || '',
          form_version: formVersion || '',
          submissionId: node.id || '',
        };
      }
    });
    return formatted;
  } else {
    return [];
  }
};

export const formatSubmissionsHeaders = (submission: any) => {
  if (submission && !isEmpty(submission)) {
    return Object.keys(submission).map((a: any) => ({
      label: a,
      key: a,
    }));
  }

  return [];
};
